import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQueryClient } from '@tanstack/react-query';
import { pdf } from '@react-pdf/renderer';
import {
  InvoiceDetailData,
  InvoiceLineItem,
  ServiceRate,
  LineItemType,
  ProgressClaim,
  ProgressClaimContext,
  ProgressClaimPhaseContext,
  ProgressClaimItemContext,
  ProgressItemClaim,
  Discipline,
  DISCIPLINES,
  disciplineLabel,
  normDiscipline as normDisc,
  sameDiscipline,
  FINAL_CLAIM_TITLE,
  computeLineItemTotals,
  formatCurrency,
  formatSiteAddress,
  isProgressClaimProjectType,
  INVOICE_STATUS_OPTIONS,
  phaseDisplayLabel,
  buildClientAddressLines,
  resolveCareOf,
  shouldMirrorRecordedDate,
  formatInvoiceDate,
  hasGapBetween,
  isUnpricedLineType,
  drawingLineText,
  parseDrawingLineText,
} from '../../config';

import InvoicePDF from '../InvoicePDF';
import LineItemEditor from './LineItemEditor';
import InvoiceSlashMenu from './InvoiceSlashMenu';
import InvoiceLineToolbar from './InvoiceLineToolbar';
import LeftSidebar from './LeftSidebar';
import ProgressNumberInput from './ProgressNumberInput';
import { useStaticData } from './useStaticData';
import { useInvoiceLoad } from './useInvoiceLoad';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';
import {
  INVOICE_PAGE_VARS,
  InvoicePagePlan,
  LINE_ITEM_LINE_PX,
  LINE_ITEM_FIRST_ROW_PAD_PX,
} from '../../invoiceLayout';
import './invoice-editor.scss';

/**
 * What changes on a line when its type does. An unpriced type drops the
 * figures. Leaving the drawing type folds the number and issue back into the
 * text ("5910-13A-300  Issue C  Title") instead of dropping them; entering it
 * splits text in that form back into the three cells, and otherwise keeps the
 * text as the title, to be given a number and issue.
 */
const lineTypeChange = (current: InvoiceLineItem | undefined, type: LineItemType): Partial<InvoiceLineItem> => {
  const unpriced = isUnpricedLineType(type);
  const updates: Partial<InvoiceLineItem> = {
    line_type: type,
    quantity: unpriced ? null : (current?.quantity ?? 1),
    rate: unpriced ? null : current?.rate,
    amount: unpriced ? null : current?.amount,
  };
  if (current?.line_type === 'drawing' && type !== 'drawing') {
    updates.description = drawingLineText(current);
    updates.drawing_number = null;
    updates.issue_number = null;
  }
  if (current && current.line_type !== 'drawing' && type === 'drawing') {
    const parts = parseDrawingLineText(current.description);
    if (parts) Object.assign(updates, parts);
  }
  return updates;
};

type SidebarTab = 'templates' | 'items' | 'fields' | 'progress';

/** How long a just-inserted line keeps the caret through re-pagination. */
const FOCUS_HOLD_MS = 400;

// Panel tab glyphs. Defined once at module scope so the segmented control and
// the collapsed rail render the identical element.
const svgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const ICON_TEMPLATES = (
  <svg {...svgProps}>
    <rect x="2" y="2" width="12" height="12" rx="2" />
    <line x1="2" y1="6" x2="14" y2="6" />
    <line x1="6.5" y1="6" x2="6.5" y2="14" />
  </svg>
);

const ICON_ITEMS = (
  <svg {...svgProps}>
    <line x1="6" y1="4.5" x2="14" y2="4.5" />
    <line x1="6" y1="8" x2="14" y2="8" />
    <line x1="6" y1="11.5" x2="14" y2="11.5" />
    <circle cx="3" cy="4.5" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="3" cy="8" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="3" cy="11.5" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const ICON_FIELDS = (
  <svg {...svgProps}>
    <path d="M2.5 4h11M2.5 8h7M2.5 12h9" />
    <circle cx="12.5" cy="8" r="1.75" />
  </svg>
);

const ICON_PROGRESS = (
  <svg {...svgProps}>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 1.5a6.5 6.5 0 0 1 6.5 6.5H8z" fill="currentColor" stroke="none" />
  </svg>
);

const ICON_PLUS = (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="8" y1="3" x2="8" y2="13" />
    <line x1="3" y1="8" x2="13" y2="8" />
  </svg>
);

const ICON_TEMPLATE_CARD = (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="12" height="13" rx="1.5" />
    <line x1="4.5" y1="5" x2="11.5" y2="5" />
    <line x1="4.5" y1="8" x2="11.5" y2="8" />
    <line x1="4.5" y1="11" x2="9" y2="11" />
  </svg>
);

const STATE_ABBREVIATIONS: Record<string, string> = {
  'western australia': 'WA', 'new south wales': 'NSW', 'victoria': 'VIC',
  'queensland': 'QLD', 'south australia': 'SA', 'tasmania': 'TAS',
  'northern territory': 'NT', 'australian capital territory': 'ACT',
};

const abbreviateState = (state?: string | null): string | null => {
  if (!state) return null;
  return STATE_ABBREVIATIONS[state.toLowerCase()] || state;
};

/** Measured heights that drive the page distribution, in CSS px. */
interface PageLayoutMetrics {
  capacity: number;
  head: number;
  thead: number;
  addArea: number;
  summary: number;
  rows: number[];
}

/**
 * One rendered A4 page: the shared plan the PDF also consumes, plus the
 * click-to-add zone, which exists only in the editor.
 */
interface InvoicePage extends InvoicePagePlan {
  showAddArea: boolean;
}

/**
 * getBoundingClientRect resolves against device pixels, so the same content
 * measures a hair taller or shorter depending on the fractional offset it
 * happens to sit at - 30.772 px on one page, 30.781 px on the next. Compared
 * exactly, that jitter reads as a relayout, so the measure/paginate loop below
 * can spend its whole pass budget and freeze a half-finished layout onto the
 * page. Anything under this is measurement noise, not a relayout.
 */
const MEASURE_EPSILON_PX = 0.1;
const sameHeight = (a: number, b: number) => Math.abs(a - b) < MEASURE_EPSILON_PX;

const InvoiceEditor: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();

  const {
    invoiceData,
    fields,
    setLineItems,
    setClaimTitle,
    setServiceDescription,
    setPoNumber,
    setInvoiceDate,
    setRecordedDate,
    setContactOverride,
    setProjectDescriptionOverride,
    setSiteAddressOverride,
    setCareOfOverride,
    refetch: fetchInvoiceDetail,
    lastSeededFields,
    refreshProjectData,
    refreshPoFromProject,
    loading,
    error,
  } = useInvoiceLoad(invoiceId);
  const { lineItems, claimTitle, serviceDescription, poNumber, invoiceDate, recordedDate, contactOverride, projectDescriptionOverride, siteAddressOverride, careOfOverride } = fields;

  // Recorded date mirrors the invoice date until the user sets it to something
  // different. Heuristic: only carry an invoice-date change across to the
  // recorded date while the two are still in sync (or recorded is blank). Once
  // they diverge — i.e. the recorded date was deliberately overridden — editing
  // the invoice date leaves the recorded date alone. No extra state needed; the
  // in-sync test survives reloads because it reads the persisted values.
  const handleInvoiceDateChange = useCallback((newDate: string) => {
    setRecordedDate(prev => shouldMirrorRecordedDate(prev, invoiceDate) ? newDate : prev);
    setInvoiceDate(newDate);
  }, [invoiceDate, setInvoiceDate, setRecordedDate]);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Dirty tracking: changeCounter counts edits, savedCounter is the count at
  // the last successful save. They differ exactly while something is unsaved,
  // which survives a save that is overtaken by further typing.
  const changeCounterRef = useRef(0);
  const savedCounterRef = useRef(0);
  // Saves are serialised through this promise so two of them can never build
  // payloads from the same stale state, and every caller awaiting a save gets
  // the outcome of its own write.
  const savePromiseRef = useRef<Promise<boolean> | null>(null);

  const { serviceRates, teamMembers, getChargeRate } = useStaticData();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>('templates');
  // LHS panel's active tab lives here so it survives the brief
  // loading-state remount when navigating between sibling invoices.
  const [lhsActiveTab, setLhsActiveTab] = useState<'time' | 'invoices'>('time');
  const [progressContext, setProgressContext] = useState<ProgressClaimContext | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [stageDrawings, setStageDrawings] = useState<{ id: number; drawing_number: string; issue_number: string; title: string; sort_order: number }[]>([]);

  // Editor lock conditions:
  //  - bundled into another invoice → document lives on the parent
  //  - status is Sent / Entered → frozen until reverted to Draft
  // Both states block the editor's auto-save, the prior_claims_deduction
  // sync effect, and the line-item editing UI.
  const isLockedBySent = !!invoiceData && invoiceData.invoice.status !== 'Draft';
  const isLockedByBundling = !!invoiceData?.bundling_target;
  const isLocked = isLockedBySent || isLockedByBundling;

  // Fetch progress claim context for Design/Advisory projects
  const fetchProgressContext = useCallback(async () => {
    if (!invoiceId || !invoiceData?.project) return;
    if (!isProgressClaimProjectType(invoiceData.project.types)) return;
    try {
      const { data, error: rpcError } = await supabase.rpc('get_progress_claim_context', {
        p_invoice_id: parseInt(invoiceId),
      });
      if (rpcError) throw rpcError;
      if (data) setProgressContext(data as ProgressClaimContext);
    } catch (err) {
      console.error('Error fetching progress claim context:', err);
    }
  }, [invoiceId, invoiceData, supabase]);

  // Fetch drawings for RWD projects
  const fetchDrawings = useCallback(async () => {
    if (!invoiceData?.project || invoiceData.project.types !== 'RWD') return;
    const stageId = invoiceData.stage?.id || invoiceData.invoice?.stage_id;
    if (!stageId) return;
    try {
      const { data } = await supabase
        .from('project_drawings')
        .select('id, drawing_number, issue_number, title, sort_order')
        .eq('stage_id', stageId)
        .order('sort_order');
      if (data) setStageDrawings(data);
    } catch (err) {
      console.error('Error fetching drawings:', err);
    }
  }, [invoiceData, supabase]);

  useEffect(() => {
    if (invoiceData?.project?.types === 'RWD') {
      fetchDrawings();
    }
  }, [invoiceData, fetchDrawings]);

  useEffect(() => {
    if (isProgressClaimProjectType(invoiceData?.project?.types)) {
      fetchProgressContext();
    }
  }, [invoiceData, fetchProgressContext]);

  // Clone prior invoice line items on first open of a fresh invoice.
  // Every line — text, items, phases, the deduction — is cloned verbatim so
  // positions match the prior invoice. Only the "Less previous claims" amount
  // is refreshed to reflect the new prior_amount_to_date sum.
  const hasSeededRef = useRef(false);
  // When set, the lineItems-watcher effect below forces an immediate save
  // (bypassing the 2s auto-save debounce) so a fast user navigation can't
  // discard the freshly-cloned content before it lands in the DB.
  const seedSavePendingRef = useRef(false);

  // Reset seed state on invoice change so a re-visit can re-clone if the
  // initial save was interrupted. The component stays mounted across route
  // changes (route param invoiceId), so refs would otherwise persist.
  useEffect(() => {
    hasSeededRef.current = false;
    seedSavePendingRef.current = false;
    setProgressContext(null);
  }, [invoiceId]);

  useEffect(() => {
    if (!progressContext) return;
    if (hasSeededRef.current) return;

    // Skip if invoice already has its own content
    if (lineItems.length > 0) {
      hasSeededRef.current = true;
      return;
    }

    // Disbursements are per-invoice: they belong to the claim they were entered
    // on and are NOT carried forward. Every other line — text, phases, and
    // count-based item lines (which are cumulative-to-date, like phases) — is
    // cloned verbatim so the new invoice starts from the prior running total;
    // the "Less previous claims" amount is then refreshed below.
    const priorItems = (progressContext.prior_line_items ?? [])
      .filter(item => item.line_type !== 'disbursement');
    if (priorItems.length === 0) return;

    hasSeededRef.current = true;

    // Sum-based LPC: cumulative net `our_fee` across prior Sent/Entered,
    // non-bundled invoices on this project+stage. Captures non-phase items
    // (regular fees, etc.) that the prior per-phase math missed, and works
    // for modal-only historical invoices that have no progress_claims rows.
    const totalPriorAmount = progressContext.prior_invoiced_total ?? 0;

    const isLessClaimsLine = (item: InvoiceLineItem): boolean =>
      item.line_type === 'prior_claims_deduction'
      || (item.line_type === 'deduction' && item.description.trim() === 'Less previous claims');

    const cloned: InvoiceLineItem[] = priorItems.map((item, i) => {
      if (isLessClaimsLine(item)) {
        return {
          ...item,
          sort_order: i,
          id: undefined,
          line_type: 'prior_claims_deduction' as LineItemType,
          description: 'Less previous claims',
          quantity: 1,
          rate: totalPriorAmount,
          amount: totalPriorAmount,
        };
      }
      return { ...item, sort_order: i, id: undefined };
    });

    // Prior invoice predates the deduction pattern (e.g. invoice #1 of the
    // project has nothing to deduct). Append spacer + deduction so net = $0.
    const hasDeduction = cloned.some(isLessClaimsLine);
    if (totalPriorAmount > 0 && !hasDeduction) {
      cloned.push({
        sort_order: cloned.length,
        line_type: 'spacer',
        description: '',
        quantity: null,
        rate: null,
        amount: null,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 0,
      });
      cloned.push({
        sort_order: cloned.length,
        line_type: 'prior_claims_deduction',
        description: 'Less previous claims',
        quantity: 1,
        rate: totalPriorAmount,
        amount: totalPriorAmount,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 0,
      });
    }

    seedSavePendingRef.current = true;
    setLineItems(cloned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressContext]);

  // Computed totals
  const totals = useMemo(() => computeLineItemTotals(lineItems), [lineItems]);

  // Active phases = those with a non-zero quoted fee. Zero-fee phases aren't
  // part of the project's actual scope, so we hide them from the Progress tab,
  // skip them when seeding from prior invoices, and exclude them from
  // applyProgressClaim output.
  const activePhases = useMemo(
    () => progressContext?.phases.filter(p => (p.quoted_fee ?? 0) > 0) ?? [],
    [progressContext]
  );

  // Locate the phase line item for a given fee section. Standard phases
  // are uniquely keyed by their enum value. Custom phases all share
  // `phase: 'Custom'`, so we disambiguate by reading the user-supplied
  // section name out of the paired phase_label description that sits
  // immediately above the phase line.
  const findPhaseLineIdx = useCallback((lis: InvoiceLineItem[], phaseCtx: ProgressClaimPhaseContext): number => {
    if (phaseCtx.phase !== 'Custom') {
      // Standard phases are keyed by enum value AND discipline — a structural
      // and a civil section can share the same phase but are distinct lines.
      return lis.findIndex(x => x.line_type === 'phase' && x.phase === phaseCtx.phase
        && sameDiscipline(x.discipline, phaseCtx.discipline));
    }
    const namePrefix = `${phaseDisplayLabel('Custom', phaseCtx.section_name)} - `;
    for (let i = 0; i < lis.length; i++) {
      if (
        lis[i].line_type === 'phase_label' &&
        lis[i].phase === 'Custom' &&
        lis[i].description.startsWith(namePrefix) &&
        i + 1 < lis.length &&
        lis[i + 1].line_type === 'phase' &&
        lis[i + 1].phase === 'Custom' &&
        sameDiscipline(lis[i + 1].discipline, phaseCtx.discipline)
      ) {
        return i + 1;
      }
    }
    return -1;
  }, []);

  // Locate the item line for an item-based section. Item lines are matched by
  // (phase, discipline, description) — the same key used for cross-invoice
  // count tracking — since each item section holds exactly one item.
  const findItemLineIdx = useCallback((lis: InvoiceLineItem[], itemCtx: ProgressClaimItemContext): number =>
    lis.findIndex(x => x.line_type === 'item'
      && x.phase === itemCtx.phase
      && sameDiscipline(x.discipline, itemCtx.discipline)
      && x.description === itemCtx.description), []);

  // Phase data is canonical on lineItems (line_type='phase' + phase column).
  // progressClaims is derived for read consumers (Progress tab UI, save payload).
  const progressClaims = useMemo<ProgressClaim[]>(() => {
    if (!progressContext) return [];
    const out: ProgressClaim[] = [];
    for (const phaseCtx of activePhases) {
      const idx = findPhaseLineIdx(lineItems, phaseCtx);
      if (idx === -1) continue;
      const li = lineItems[idx];
      const pct = Math.round((li.quantity ?? 0) * 100 * 100) / 100;
      const amountToDate = Math.round((phaseCtx.quoted_fee * pct) / 100 * 100) / 100;
      const amountThisPeriod = Math.round((amountToDate - phaseCtx.prior_amount_to_date) * 100) / 100;
      out.push({
        phase: phaseCtx.phase,
        discipline: phaseCtx.discipline ?? null,
        fee_id: progressContext.accepted_fee_id ?? null,
        section_id: phaseCtx.section_id,
        quoted_fee: phaseCtx.quoted_fee,
        percent_complete: pct,
        amount_to_date: amountToDate,
        amount_this_period: amountThisPeriod,
        sort_order: phaseCtx.sort_order,
      });
    }
    return out;
  }, [lineItems, progressContext, activePhases, findPhaseLineIdx]);

  // Item-based sections, claimed by count. Like phases, `count` is the
  // cumulative to-date count, canonical on the matching item line (its
  // quantity), mirroring how progressClaims reads cumulative % off the phase
  // line. `remaining` = quoted count − this invoice's to-date count.
  const activeItems = useMemo<ProgressItemClaim[]>(() => {
    if (!progressContext) return [];
    return (progressContext.items ?? [])
      // Mirror activePhases' "has a value" filter: a zero-quota item isn't
      // claimable, so it shouldn't drive the Progress tab or its empty states.
      .filter(it => (it.qty_total ?? 0) > 0)
      .map(it => {
        const idx = findItemLineIdx(lineItems, it);
        const count = idx === -1 ? 0 : (lineItems[idx].quantity ?? 0);
        const remaining = Math.max(0, (it.qty_total ?? 0) - count);
        return { ...it, remaining, count };
      });
  }, [lineItems, progressContext, findItemLineIdx]);

  // The Less previous claims line is set by the seeding effect (above) on
  // first creation and is editable thereafter. The user can change it
  // directly in the line items editor, or click the row's Recompute button
  // to reset it to the current `prior_invoiced_total`. There is no auto-sync
  // — that would clobber the user's manual override.

  // Bundling now means: this invoice's line items represent the FULL bill;
  // bundled prior drafts (e.g. Jan $2k) are just there for period attribution.
  // The PDF prints only this invoice's items; the bundled drafts contribute
  // nothing to the rendered document. The "this period allocation" is the
  // amount of this invoice that's attributable to this period — i.e. own
  // total minus the sum of bundled drafts' totals — shown in the editor for
  // transparency, never on the PDF.
  const hasBundledChildren = (invoiceData?.bundled_invoices?.length ?? 0) > 0;

  const bundledOurFee = useMemo(
    () => (invoiceData?.bundled_invoices ?? []).reduce((sum, b) => sum + b.our_fee, 0),
    [invoiceData?.bundled_invoices]
  );

  const allocationTotals = useMemo(() => {
    // Net of deductions and bundled prior totals — the actual revenue
    // attributable to this invoice's period.
    const subtotal = totals.subtotal - bundledOurFee;
    const gst = subtotal * 0.10;
    const total = subtotal + gst;
    return { subtotal, gst, total };
  }, [totals.subtotal, bundledOurFee]);

  // Line item operations
  const addLineItem = useCallback((type: LineItemType = 'item') => {
    const newItem: InvoiceLineItem = {
      sort_order: lineItems.length,
      line_type: type,
      description: '',
      quantity: isUnpricedLineType(type) ? null : 1,
      rate: null,
      amount: null,
      team_member_id: null,
      service_rate_id: null,
      indent_level: 0,
    };
    setLineItems(prev => [...prev, newItem]);
  }, [lineItems.length, setLineItems]);

  const addFromServiceRate = useCallback((rate: ServiceRate) => {
    const newItem: InvoiceLineItem = {
      sort_order: lineItems.length,
      line_type: 'item',
      description: rate.description,
      quantity: 1,
      rate: rate.rate,
      amount: rate.rate,
      team_member_id: null,
      service_rate_id: rate.id,
      indent_level: 0,
    };
    setLineItems(prev => [...prev, newItem]);
  }, [lineItems.length, setLineItems]);

  const addHourlyCharge = useCallback((teamMemberId: string) => {
    const member = teamMembers.find(m => m.id === teamMemberId);
    const rate = getChargeRate(teamMemberId);
    if (!member) return;

    const newItem: InvoiceLineItem = {
      sort_order: lineItems.length,
      line_type: 'item',
      description: `${member.first_name} ${member.last_name} - Engineering Hours`,
      quantity: 0,
      rate: rate,
      amount: 0,
      team_member_id: teamMemberId,
      team_member_name: `${member.first_name} ${member.last_name}`,
      team_member_type: member.type,
      service_rate_id: null,
      indent_level: 0,
    };
    setLineItems(prev => [...prev, newItem]);
  }, [lineItems.length, teamMembers, getChargeRate, setLineItems]);

  const updateLineItem = useCallback((index: number, updates: Partial<InvoiceLineItem>) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };

      // Auto-calculate amount when quantity or rate change. Both must be set
      // so a user-entered amount survives partial edits (e.g. typing amount,
      // then qty, then rate — without rate the multiply would zero it).
      if (
        ('quantity' in updates || 'rate' in updates) &&
        !isUnpricedLineType(updated[index].line_type) &&
        updated[index].quantity != null &&
        updated[index].rate != null
      ) {
        const qty = updated[index].quantity as number;
        const rt = updated[index].rate as number;
        updated[index].amount = Math.round(qty * rt * 100) / 100;
      }

      // For phase lines, sync the paired phase_label above (if any) with the new %.
      // For Custom phases, preserve the user-supplied prefix (e.g. "Site Inspections")
      // by extracting it from the existing label rather than re-using the enum value.
      const li = updated[index];
      if (li.line_type === 'phase' && li.phase && 'quantity' in updates && index > 0) {
        const pct = Math.round((li.quantity ?? 0) * 100 * 100) / 100;
        const above = updated[index - 1];
        const isPairedLabel =
          (above.line_type === 'phase_label' && above.phase === li.phase) ||
          // Legacy fallback for invoices created before phase_label existed
          (above.line_type === 'text' &&
            new RegExp(`^${li.phase}\\s*-.*Complete$`, 'i').test(above.description.trim()));
        if (isPairedLabel) {
          const prefix = li.phase === 'Custom'
            ? (above.description.split(' - ')[0] || 'Custom phase')
            : li.phase;
          updated[index - 1] = { ...above, description: `${prefix} - ${pct}% Complete` };
        }
      }

      return updated;
    });
  }, [setLineItems]);

  const removeLineItem = useCallback((index: number) => {
    setLineItems(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((item, i) => ({ ...item, sort_order: i }));
    });
  }, [setLineItems]);

  const moveLineItem = useCallback((from: number, to: number) => {
    setLineItems(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated.map((item, i) => ({ ...item, sort_order: i }));
    });
  }, [setLineItems]);

  // A line just inserted, to be focused once it is in the DOM (see the layout
  // effect after measureLayout).
  const pendingFocusRef = useRef<{ index: number; until: number } | null>(null);

  const insertLineItemAfter = useCallback((afterIndex: number, type: string) => {
    setLineItems(prev => {
      const newItem: InvoiceLineItem = {
        sort_order: 0,
        line_type: type as LineItemType,
        description: '',
        quantity: null,
        rate: null,
        amount: null,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 0,
      };
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, newItem);
      return updated.map((item, i) => ({ ...item, sort_order: i }));
    });

    // Focused by the layout effect below, in the same commit that adds the
    // row. This used to be a 50ms setTimeout, and anything typed straight
    // after Enter in that window went on the end of the line above.
    pendingFocusRef.current = { index: afterIndex + 1, until: performance.now() + FOCUS_HOLD_MS };
  }, [setLineItems]);

  // Slash command menu state
  const [slashMenu, setSlashMenu] = useState<{
    isOpen: boolean;
    position: { top: number; left: number };
    searchQuery: string;
    targetIndex: number | null;
  }>({ isOpen: false, position: { top: 0, left: 0 }, searchQuery: '', targetIndex: null });

  const closeSlashMenu = useCallback(() => {
    setSlashMenu({ isOpen: false, position: { top: 0, left: 0 }, searchQuery: '', targetIndex: null });
  }, []);

  const handleSlashSelect = useCallback((type: LineItemType) => {
    if (slashMenu.targetIndex !== null) {
      insertLineItemAfter(slashMenu.targetIndex, type);
    }
    closeSlashMenu();
  }, [slashMenu.targetIndex, insertLineItemAfter, closeSlashMenu]);

  const handleSlashChangeType = useCallback((type: LineItemType) => {
    if (slashMenu.targetIndex !== null) {
      updateLineItem(slashMenu.targetIndex, lineTypeChange(lineItems[slashMenu.targetIndex], type));
    }
    closeSlashMenu();
  }, [slashMenu.targetIndex, updateLineItem, lineItems, closeSlashMenu]);

  const handleSlashDelete = useCallback(() => {
    if (slashMenu.targetIndex !== null) {
      removeLineItem(slashMenu.targetIndex);
    }
    closeSlashMenu();
  }, [slashMenu.targetIndex, removeLineItem, closeSlashMenu]);

  // Line toolbar state (floating action bar on focus)
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });

  const handleRowFocus = useCallback((index: number, rowElement: HTMLTableRowElement) => {
    const container = rowElement.closest('.invoice-preview');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const rowRect = rowElement.getBoundingClientRect();
    setActiveLineIndex(index);
    const top = rowRect.top - containerRect.top;
    const left = rowRect.left - containerRect.left;
    setToolbarPosition(prev => {
      if (prev.top === top && prev.left === left) return prev;
      return { top, left };
    });
  }, []);

  // Close toolbar when clicking anywhere that isn't a line item row or the toolbar
  useEffect(() => {
    const handleDismiss = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.closest('.invoice-line-toolbar')) return;
      if (target.closest('.line-item-row')) return;
      setActiveLineIndex(null);
    };
    document.addEventListener('mousedown', handleDismiss);
    document.addEventListener('click', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handleDismiss);
      document.removeEventListener('click', handleDismiss);
    };
  }, []);

  const handleToolbarTypeChange = useCallback((type: LineItemType) => {
    if (activeLineIndex === null) return;
    updateLineItem(activeLineIndex, lineTypeChange(lineItems[activeLineIndex], type));
  }, [activeLineIndex, lineItems, updateLineItem]);

  const handleToolbarTeamMemberChange = useCallback((memberId: string, memberName: string, rate: number | null) => {
    if (activeLineIndex === null) return;
    if (!memberId) {
      updateLineItem(activeLineIndex, { team_member_id: null, team_member_name: null });
      return;
    }
    updateLineItem(activeLineIndex, {
      team_member_id: memberId,
      team_member_name: memberName,
      rate: rate,
      amount: rate ? (lineItems[activeLineIndex]?.quantity ?? 0) * rate : lineItems[activeLineIndex]?.amount,
    });
  }, [activeLineIndex, lineItems, updateLineItem]);

  const handleToolbarDelete = useCallback(() => {
    if (activeLineIndex === null) return;
    removeLineItem(activeLineIndex);
    setActiveLineIndex(null);
  }, [activeLineIndex, removeLineItem]);

  const handleToolbarIndent = useCallback((level: number) => {
    if (activeLineIndex === null) return;
    updateLineItem(activeLineIndex, { indent_level: level });
  }, [activeLineIndex, updateLineItem]);

  // The buttons act on the GAP they name, not on this line's flag in
  // isolation. Opening one only needs this line to ask for it; closing one has
  // to clear both sides, because either side alone would keep it open. Without
  // that, turning a gap off from the line you are looking at would appear to do
  // nothing whenever its neighbour was the side asking for it.
  const handleToolbarToggleSpacing = useCallback((side: 'above' | 'below') => {
    if (activeLineIndex === null) return;
    setLineItems(prev => {
      const item = prev[activeLineIndex];
      if (!item) return prev;
      const neighbourIndex = side === 'above' ? activeLineIndex - 1 : activeLineIndex + 1;
      const neighbour = prev[neighbourIndex];
      const gapExists = side === 'above'
        ? hasGapBetween(neighbour, item)
        : !!neighbour && hasGapBetween(item, neighbour);

      const next = [...prev];
      const ownField = side === 'above' ? 'space_above' : 'space_below';
      next[activeLineIndex] = { ...item, [ownField]: !gapExists };
      if (gapExists && neighbour) {
        const neighbourField = side === 'above' ? 'space_below' : 'space_above';
        next[neighbourIndex] = { ...neighbour, [neighbourField]: false };
      }
      return next;
    });
  }, [activeLineIndex, setLineItems]);

  // Delete tax invoice document (line items + progress claims) but keep the
  // register row. our_fee / disbursements are preserved so the row continues
  // to show its recorded total — user can adjust via the Detail Modal if
  // they want a different amount.
  const [showDeleteTaxInvoiceConfirm, setShowDeleteTaxInvoiceConfirm] = useState(false);
  const [isDeletingTaxInvoice, setIsDeletingTaxInvoice] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const saveMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!saveMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(e.target as Node)) {
        setSaveMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSaveMenuOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [saveMenuOpen]);

  // Status control (compact dropdown in the toolbar, next to Save).
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!statusMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [statusMenuOpen]);

  const handleDeleteTaxInvoice = useCallback(async () => {
    if (!invoiceData || !invoiceId) return;
    try {
      setIsDeletingTaxInvoice(true);
      const { error } = await supabase.rpc('update_invoice_with_line_items', {
        p_invoice_id: parseInt(invoiceId),
        p_invoice: {
          our_fee: invoiceData.invoice.our_fee,
          disbursements: invoiceData.invoice.disbursements,
          invoice_date: invoiceData.invoice.invoice_date,
          status: invoiceData.invoice.status,
          notes: invoiceData.invoice.notes,
          claim_title: invoiceData.invoice.claim_title,
          claim_description: invoiceData.invoice.claim_description,
          service_description: invoiceData.invoice.service_description,
          po_number: invoiceData.invoice.po_number,
          contact_override: invoiceData.invoice.contact_override,
          project_description_override: invoiceData.invoice.project_description_override,
          site_address_override: invoiceData.invoice.site_address_override,
        },
        p_line_items: [],
        p_progress_claims: [],
      });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['get_filtered_invoices'] });
      setShowDeleteTaxInvoiceConfirm(false);
      handleClose();
    } catch (err) {
      console.error('Error deleting tax invoice:', err);
    } finally {
      setIsDeletingTaxInvoice(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData, invoiceId, supabase, queryClient]);

  // Everything the save payload is built from. doSave reads this rather than
  // its own closure, so a queued save, an auto-save that fires after further
  // typing, or the flush on close all write what is on screen now.
  const savePayloadRef = useRef({
    invoiceData, invoiceId, lineItems, totals, invoiceDate, recordedDate, claimTitle,
    serviceDescription, poNumber, contactOverride, projectDescriptionOverride,
    siteAddressOverride, careOfOverride, progressClaims, progressContext,
  });
  savePayloadRef.current = {
    invoiceData, invoiceId, lineItems, totals, invoiceDate, recordedDate, claimTitle,
    serviceDescription, poNumber, contactOverride, projectDescriptionOverride,
    siteAddressOverride, careOfOverride, progressClaims, progressContext,
  };

  // Save (quiet = true for auto-save, avoids re-fetching and resetting state).
  // Resolves true only when the write actually landed — callers that gate an
  // irreversible follow-up action on the save (e.g. locking the invoice by
  // marking it Sent) must check the result, since errors are handled here and
  // never propagate.
  const doSave = useCallback(async (quiet = false): Promise<boolean> => {
    const {
      invoiceData, invoiceId, lineItems, totals, invoiceDate, recordedDate, claimTitle,
      serviceDescription, poNumber, contactOverride, projectDescriptionOverride,
      siteAddressOverride, careOfOverride, progressClaims, progressContext,
    } = savePayloadRef.current;
    if (!invoiceData || !invoiceId) return false;

    const counterAtStart = changeCounterRef.current;
    setSaving(true);
    if (!quiet) setSaveStatus('saving');

    try {
      const lineItemsPayload = lineItems.map((item, index) => ({
        id: item.id || null,
        sort_order: index,
        line_type: item.line_type,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount,
        team_member_id: item.team_member_id,
        service_rate_id: item.service_rate_id,
        indent_level: item.indent_level ?? 0,
        // Null keeps the line on its type's default rather than freezing
        // today's default into the row.
        space_above: item.space_above ?? null,
        space_below: item.space_below ?? null,
        phase: item.phase ?? null,
        discipline: item.discipline ?? null,
        // Only a drawing line carries these; a stale value on another type
        // would print nowhere but still be saved.
        drawing_number: item.line_type === 'drawing' ? (item.drawing_number ?? null) : null,
        issue_number: item.line_type === 'drawing' ? (item.issue_number ?? null) : null,
      }));

      const rpcParams = {
        p_invoice_id: parseInt(invoiceId),
        p_invoice: {
          our_fee: totals.ourFee,
          disbursements: totals.disbursements,
          invoice_date: invoiceDate || null,
          // Clearing the recorded date falls back to the sent date (matches the
          // Add/Detail modals). Final `|| null` guards the all-empty edge so we
          // never send '' into the RPC's ::date cast.
          recorded_date: recordedDate || invoiceDate || null,
          claim_title: claimTitle || null,
          service_description: serviceDescription || null,
          po_number: poNumber || null,
          contact_override: contactOverride || null,
          project_description_override: projectDescriptionOverride || null,
          site_address_override: siteAddressOverride || null,
          care_of_override: careOfOverride || null,
        },
        p_line_items: lineItemsPayload,
        // Always send the array (even when empty) so deleting all phase
        // lines on a Design/Advisory invoice actually wipes its progress
        // claim rows. The RPC treats `null` as "preserve" — that's only
        // appropriate when this isn't a progress-claim invoice at all.
        p_progress_claims: progressContext ? progressClaims.map((c, idx) => ({
          phase: c.phase,
          discipline: c.discipline ?? null,
          fee_id: c.fee_id,
          section_id: c.section_id,
          quoted_fee: c.quoted_fee,
          percent_complete: c.percent_complete,
          amount_to_date: c.amount_to_date,
          amount_this_period: c.amount_this_period,
          sort_order: idx,
        })) : null,
      };
      const { error: saveError } = await supabase.rpc('update_invoice_with_line_items', rpcParams);

      if (saveError) throw saveError;

      // Invalidate the invoice register's cached rows so totals refresh on return
      queryClient.invalidateQueries({ queryKey: ['get_filtered_invoices'] });

      savedCounterRef.current = counterAtStart;
      // Only clear the flag if nothing was typed while the write was in flight
      if (changeCounterRef.current === counterAtStart) setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      return true;
    } catch (err) {
      console.error('Error saving invoice:', err);
      notifyReadOnly(err);
      setSaveStatus('error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [supabase, queryClient]);

  /**
   * Save, queued behind any save already in flight. Callers may await the
   * result: it reflects their own write, not an earlier one.
   */
  const handleSave = useCallback((quiet = false): Promise<boolean> => {
    const previous = savePromiseRef.current ?? Promise.resolve(true);
    const run = previous.catch(() => false).then(() => doSave(quiet));
    savePromiseRef.current = run;
    return run.finally(() => {
      if (savePromiseRef.current === run) savePromiseRef.current = null;
    });
  }, [doSave]);

  // Persist the seeded clone immediately rather than waiting for the 2s
  // auto-save debounce. Without this, a user clicking a sibling invoice
  // in the LHS panel within 2s of opening the freshly-cloned invoice would
  // cancel the pending auto-save and lose the seeded line items.
  useEffect(() => {
    if (!seedSavePendingRef.current) return;
    if (lineItems.length === 0) return; // wait for the seed setLineItems to commit
    seedSavePendingRef.current = false;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    void handleSave(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  // Generate PDF
  const handleGeneratePdf = useCallback(async () => {
    if (!invoiceData) return;

    try {
      setIsGeneratingPdf(true);
      setPdfError(null);

      // Save first to ensure latest data
      await handleSave();

      const pdfData: InvoiceDetailData = {
        ...invoiceData,
        invoice: {
          ...invoiceData.invoice,
          our_fee: totals.ourFee,
          disbursements: totals.disbursements,
          gst_amount: totals.gst,
          total_amount: totals.total,
          invoice_date: invoiceDate || invoiceData.invoice.invoice_date,
          recorded_date: recordedDate || invoiceData.invoice.recorded_date,
          claim_title: claimTitle || null,
          service_description: serviceDescription || null,
          po_number: poNumber || null,
          contact_override: contactOverride || null,
          project_description_override: projectDescriptionOverride || null,
          site_address_override: siteAddressOverride || null,
          care_of_override: careOfOverride || null,
        },
        line_items: lineItems,
      };

      // Hand the PDF the page split the editor is showing, so the export
      // breaks exactly where the preview says it will.
      const pagePlans: InvoicePagePlan[] = invoicePagesRef.current.map(page => ({
        rows: page.rows,
        showSummary: page.showSummary,
      }));

      // The Tinos faces are fetched at render time. If they cannot be had -
      // a bad deploy, an offline client - retry with a built-in family rather
      // than losing the invoice: the metrics stop matching the preview
      // exactly, but the user gets their PDF.
      let blob: Blob;
      try {
        blob = await pdf(<InvoicePDF data={pdfData} pages={pagePlans} />).toBlob();
      } catch (fontErr) {
        console.error('PDF font load failed; retrying with Times-Roman:', fontErr);
        blob = await pdf(
          <InvoicePDF data={pdfData} pages={pagePlans} fontFamily="Times-Roman" />
        ).toBlob();
        setPdfError('Invoice fonts could not be loaded; the PDF uses a substitute face.');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceData.invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Previously this only reached the console, so a failed export looked
      // exactly like a click that never registered.
      console.error('Error generating PDF:', err);
      setPdfError('Could not generate the PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [invoiceData, handleSave, totals, invoiceDate, recordedDate, claimTitle, serviceDescription, poNumber, contactOverride, projectDescriptionOverride, siteAddressOverride, careOfOverride, lineItems]);

  // Close handler
  const handleClose = useCallback(() => {
    // `returnTo` is set by callers outside the register (the job card modal
    // on Budget / Fees / Job Cards) so Close goes back where the user came
    // from. Only an in-app absolute path is honoured. `reopenJobCard` is the
    // modal's own description of itself — opaque here, handed straight back
    // so the host page can reopen it on the tab it was left on.
    const state = location.state as
      | { fromSearch?: string; returnTo?: string; reopenJobCard?: unknown }
      | null;
    const returnTo = state?.returnTo;
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      navigate(returnTo, { state: { reopenJobCard: state?.reopenJobCard } });
      return;
    }
    navigate(`/invoices${state?.fromSearch ?? ''}`);
  }, [navigate, location.state]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, handleSave]);

  // Debounced auto-save: triggers 2s after last edit
  const hasLoadedRef = useRef(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    // A fetch re-seeded the fields; that is the server's copy, not an edit
    if (fields === lastSeededFields) return;
    // Locked invoices (bundled, Sent, or Entered) reject edits at the RPC.
    // Don't auto-save them.
    if (isLocked) return;

    changeCounterRef.current += 1;
    setHasUnsavedChanges(true);

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Saves queue behind each other, so this fires on schedule even when one
    // is already in flight - an edit made mid-save is no longer stranded
    // until the next keystroke.
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      void handleSave(true);
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Change the invoice's status from the toolbar. Marking Sent/Entered freezes
  // the document (DB edit-lock trigger), so any pending edits are flushed while
  // the invoice is still Draft — otherwise the locks would reject them.
  // Reverting to Draft just unlocks. A re-fetch afterwards refreshes the lock
  // state, banners, and toolbar.
  const handleStatusChange = useCallback(async (newStatus: string) => {
    setStatusMenuOpen(false);
    if (!invoiceData || !invoiceId) return;
    const current = invoiceData.invoice.status;
    if (newStatus === current) return;
    setStatusChanging(true);
    try {
      // Cancel any queued auto-save so it can't fire against a locked invoice.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      // Persist the latest edits before locking (only Draft invoices are
      // writable). The debounced auto-save covers the header fields too — not
      // just line items — so flush unconditionally, including for invoices with
      // no line items. If the flush fails, abort: locking the invoice now would
      // strand those edits behind the edit-lock triggers.
      if (current === 'Draft' && !(await handleSave(true))) {
        return;
      }
      const { error } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .eq('id', parseInt(invoiceId));
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['get_filtered_invoices'] });
      await fetchInvoiceDetail();
    } catch (err) {
      console.error('Error changing invoice status:', err);
      notifyReadOnly(err);
    } finally {
      setStatusChanging(false);
    }
  }, [invoiceData, invoiceId, handleSave, supabase, queryClient, fetchInvoiceDetail]);

  useEffect(() => {
    if (!loading && invoiceData) {
      const timer = setTimeout(() => {
        hasLoadedRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, invoiceData]);

  // Flush pending edits when the editor closes (Escape, the close button,
  // navigating to a sibling invoice) instead of dropping the debounce with
  // them. The page is still alive here, so the ordinary save path applies.
  const flushOnCloseRef = useRef<() => void>(() => {});
  flushOnCloseRef.current = () => {
    if (isLocked) return;
    if (changeCounterRef.current === savedCounterRef.current) return;
    void handleSave(true);
  };
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      flushOnCloseRef.current();
    };
  }, []);

  // Tab close / reload: warn while edits are unsaved. A Supabase write cannot
  // be relied on to complete during unload, so the prompt is the guard.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (changeCounterRef.current === savedCounterRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Filter service rates for this project's job type
  const applicableRates = useMemo(() => {
    if (!invoiceData?.project) return [];
    return serviceRates.filter(r => r.job_type === invoiceData.project?.types);
  }, [invoiceData?.project, serviceRates]);

  // Engineers for hourly charges
  const engineers = useMemo(() => {
    return teamMembers.filter(m => m.type === 'Engineer');
  }, [teamMembers]);

  // RWC template system
  const rwcTemplateContext = useMemo(() => {
    if (!invoiceData?.project || invoiceData.project.types !== 'RWC') return null;

    const rwcRates = serviceRates.filter(r => r.job_type === 'RWC' && r.is_active);
    const proj = invoiceData.project;
    const stage = invoiceData.stage;

    const numInspections = stage?.number_of_inspections ?? proj.number_of_inspections ?? 0;
    const inspectionRate = numInspections >= 4
      ? rwcRates.find(r => r.rate_code === 'rwc_inspection_four_plus')
      : rwcRates.find(r => r.rate_code === 'rwc_inspection');

    return {
      startupRate: rwcRates.find(r => r.rate_code === 'rwc_startup_meeting'),
      inspectionRate: inspectionRate || rwcRates.find(r => r.rate_code === 'rwc_inspection'),
      qaRate: rwcRates.find(r => r.rate_code === 'rwc_qa_review'),
      hasStartup: stage?.startup_meeting ?? proj.startup_meeting,
      numInspections,
      hasQA: stage?.requires_qa ?? proj.requires_qa,
      po: stage?.po ?? proj.po,
    };
  }, [invoiceData, serviceRates]);

  const applyRWCTemplate = useCallback((templateId: 'inspections' | 'qa') => {
    if (!rwcTemplateContext) return;
    const { startupRate, inspectionRate, qaRate, hasStartup, numInspections, po } = rwcTemplateContext;

    const newItems: InvoiceLineItem[] = [];
    let sortOrder = lineItems.length;

    if (templateId === 'inspections') {
      newItems.push({
        sort_order: sortOrder++,
        line_type: 'text',
        description: 'Retaining Wall Inspections: 100% Complete',
        quantity: null, rate: null, amount: null,
        team_member_id: null, service_rate_id: null, indent_level: 0,
      });

      if (hasStartup && startupRate) {
        newItems.push({
          sort_order: sortOrder++,
          line_type: 'item',
          description: startupRate.description,
          quantity: 1,
          rate: startupRate.rate,
          amount: startupRate.rate,
          team_member_id: null,
          service_rate_id: startupRate.id,
          indent_level: 1,
        });
      }

      if (numInspections > 0 && inspectionRate) {
        newItems.push({
          sort_order: sortOrder++,
          line_type: 'item',
          description: 'Wall Inspections',
          quantity: numInspections,
          rate: inspectionRate.rate,
          amount: numInspections * inspectionRate.rate,
          team_member_id: null,
          service_rate_id: inspectionRate.id,
          indent_level: 1,
        });
      }
    }

    if (templateId === 'qa') {
      newItems.push({
        sort_order: sortOrder++,
        line_type: 'text',
        description: 'QA Review & As Constructed Certification: 100% Complete.',
        quantity: null, rate: null, amount: null,
        team_member_id: null, service_rate_id: null, indent_level: 0,
      });

      if (qaRate) {
        newItems.push({
          sort_order: sortOrder++,
          line_type: 'item',
          description: 'As previously agreed.',
          quantity: 1,
          rate: qaRate.rate,
          amount: qaRate.rate,
          team_member_id: null,
          service_rate_id: qaRate.id,
          indent_level: 1,
        });
      }
    }

    if (newItems.length > 0) {
      setLineItems(prev => [...prev, ...newItems]);
    }

    setServiceDescription('RETAINING WALL INSPECTIONS & CERTIFICATION');

    if (po && !poNumber) {
      setPoNumber(po);
    }
  }, [rwcTemplateContext, lineItems.length, poNumber, setLineItems, setPoNumber, setServiceDescription]);

  // RWD template system
  const rwdTemplateContext = useMemo(() => {
    if (!invoiceData?.project || invoiceData.project.types !== 'RWD') return null;
    if (stageDrawings.length === 0) return null;

    const rwdRates = serviceRates.filter(r => r.job_type === 'RWD' && r.is_active);
    return {
      drawings: stageDrawings,
      firstIssueRate: rwdRates.find(r => r.rate_code === 'rwd_first_issue'),
      revisionRate: rwdRates.find(r => r.rate_code === 'rwd_revision'),
      po: invoiceData.stage?.po ?? invoiceData.project?.po,
    };
  }, [invoiceData, serviceRates, stageDrawings]);

  const applyRWDTemplate = useCallback((templateId: 'first_issue' | 'revision') => {
    if (!rwdTemplateContext) return;
    const { drawings, firstIssueRate, revisionRate, po } = rwdTemplateContext;
    const rate = templateId === 'first_issue' ? firstIssueRate : revisionRate;
    if (!rate) return;

    const newItems: InvoiceLineItem[] = [];
    let sortOrder = lineItems.length;

    newItems.push({
      sort_order: sortOrder++,
      line_type: 'text',
      description: 'Structural Certification of Retaining Wall Drawings',
      quantity: null, rate: null, amount: null,
      team_member_id: null, service_rate_id: null, indent_level: 0,
    });

    // One drawing line per drawing, copied from the set as it stands now.
    for (const drawing of drawings) {
      newItems.push({
        sort_order: sortOrder++,
        line_type: 'drawing',
        drawing_number: drawing.drawing_number,
        issue_number: drawing.issue_number,
        description: drawing.title,
        quantity: null, rate: null, amount: null,
        team_member_id: null, service_rate_id: null, indent_level: 0,
      });
    }

    newItems.push({
      sort_order: sortOrder++,
      line_type: 'item',
      description: 'As previously agreed.',
      quantity: 1,
      rate: rate.rate,
      amount: rate.rate,
      team_member_id: null,
      service_rate_id: rate.id,
      indent_level: 1,
    });

    setLineItems(prev => [...prev, ...newItems]);

    if (!serviceDescription) {
      setServiceDescription('Retaining wall certificate of design compliance');
    }

    if (po && !poNumber) {
      setPoNumber(po);
    }
  }, [rwdTemplateContext, lineItems.length, serviceDescription, poNumber, setLineItems, setPoNumber, setServiceDescription]);

  // Progress claim helpers (Design / Advisory)
  // Updates the canonical phase line item (and its paired label). If no phase
  // line exists yet, inserts the phase + phase_label pair before any existing
  // prior_claims_deduction line.
  const setPhasePercent = useCallback((phaseCtx: ProgressClaimPhaseContext, pct: number) => {
    const qty = pct / 100;
    const amount = Math.round(phaseCtx.quoted_fee * pct) / 100;
    const labelDescription = `${phaseDisplayLabel(phaseCtx.phase, phaseCtx.section_name)} - ${pct}% Complete`;
    setLineItems(prev => {
      const phaseIdx = findPhaseLineIdx(prev, phaseCtx);
      if (phaseIdx !== -1) {
        const next = [...prev];
        const labelIdx = phaseIdx > 0
          && next[phaseIdx - 1].line_type === 'phase_label'
          && next[phaseIdx - 1].phase === phaseCtx.phase
          && sameDiscipline(next[phaseIdx - 1].discipline, phaseCtx.discipline)
          ? phaseIdx - 1 : -1;
        next[phaseIdx] = { ...next[phaseIdx], quantity: qty, amount };
        if (labelIdx !== -1) {
          next[labelIdx] = { ...next[labelIdx], description: labelDescription };
        }
        return next.map((li, i) => ({ ...li, sort_order: i }));
      }
      // Insert new pair before any prior_claims_deduction (or at the end)
      const insertAt = (() => {
        const dedIdx = prev.findIndex(li =>
          li.line_type === 'prior_claims_deduction' ||
          (li.line_type === 'deduction' && li.description.trim() === 'Less previous claims')
        );
        if (dedIdx === -1) return prev.length;
        // Also skip back over any spacer that immediately precedes the deduction
        return dedIdx > 0 && prev[dedIdx - 1].line_type === 'spacer' ? dedIdx - 1 : dedIdx;
      })();
      const labelLine: InvoiceLineItem = {
        sort_order: insertAt,
        line_type: 'phase_label',
        description: labelDescription,
        quantity: null,
        rate: null,
        amount: null,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 0,
        phase: phaseCtx.phase,
        discipline: phaseCtx.discipline ?? null,
      };
      const phaseLine: InvoiceLineItem = {
        sort_order: insertAt + 1,
        line_type: 'phase',
        description: 'As previously agreed',
        quantity: qty,
        rate: phaseCtx.quoted_fee,
        amount,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 1,
        phase: phaseCtx.phase,
        discipline: phaseCtx.discipline ?? null,
      };
      const next = [...prev.slice(0, insertAt), labelLine, phaseLine, ...prev.slice(insertAt)];
      return next.map((li, i) => ({ ...li, sort_order: i }));
    });
  }, [setLineItems, findPhaseLineIdx]);

  // Set the per-period count for an item-based section. Mirrors setPhasePercent:
  // writes a label + item line pair live into lineItems (creating, updating, or
  // — when the count drops to 0 — removing the pair). The item line carries
  // phase + discipline + description so it groups and matches prior claims.
  const setItemCount = useCallback((itemCtx: ProgressClaimItemContext, count: number) => {
    const amount = Math.round(count * (itemCtx.rate ?? 0) * 100) / 100;
    const labelDescription = phaseDisplayLabel(itemCtx.phase, itemCtx.section_name);
    setLineItems(prev => {
      const idx = findItemLineIdx(prev, itemCtx);
      // Count cleared → drop the item line and its paired label (if present).
      if (count <= 0) {
        if (idx === -1) return prev;
        const next = [...prev];
        const labelIdx = idx > 0
          && next[idx - 1].line_type === 'phase_label'
          && next[idx - 1].phase === itemCtx.phase
          && sameDiscipline(next[idx - 1].discipline, itemCtx.discipline)
          ? idx - 1 : -1;
        next.splice(idx, 1);
        if (labelIdx !== -1) next.splice(labelIdx, 1);
        return next.map((li, i) => ({ ...li, sort_order: i }));
      }
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: count, rate: itemCtx.rate, amount, description: itemCtx.description };
        return next.map((li, i) => ({ ...li, sort_order: i }));
      }
      // Insert a new label + item pair before any prior_claims_deduction block.
      const insertAt = (() => {
        const dedIdx = prev.findIndex(li =>
          li.line_type === 'prior_claims_deduction' ||
          (li.line_type === 'deduction' && li.description.trim() === 'Less previous claims')
        );
        if (dedIdx === -1) return prev.length;
        return dedIdx > 0 && prev[dedIdx - 1].line_type === 'spacer' ? dedIdx - 1 : dedIdx;
      })();
      const labelLine: InvoiceLineItem = {
        sort_order: insertAt,
        line_type: 'phase_label',
        description: labelDescription,
        quantity: null,
        rate: null,
        amount: null,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 0,
        phase: itemCtx.phase,
        discipline: itemCtx.discipline ?? null,
      };
      const itemLine: InvoiceLineItem = {
        sort_order: insertAt + 1,
        line_type: 'item',
        description: itemCtx.description,
        quantity: count,
        rate: itemCtx.rate,
        amount,
        team_member_id: null,
        service_rate_id: null,
        indent_level: 1,
        phase: itemCtx.phase,
        discipline: itemCtx.discipline ?? null,
      };
      const next = [...prev.slice(0, insertAt), labelLine, itemLine, ...prev.slice(insertAt)];
      return next.map((li, i) => ({ ...li, sort_order: i }));
    });
  }, [setLineItems, findItemLineIdx]);

  const applyProgressClaim = useCallback(() => {
    if (!progressContext) return;
    const totalPriorAmount = progressContext.prior_invoiced_total ?? 0;

    const isLessClaimsLine = (item: InvoiceLineItem): boolean =>
      item.line_type === 'prior_claims_deduction'
      || (item.line_type === 'deduction' && item.description.trim() === 'Less previous claims');

    // Emit discipline headings only when the project has civil scope (i.e. an
    // active civil phase or item exists). Structural-only invoices stay heading-free.
    const itemsToClaim = activeItems.filter(it => it.count > 0);
    const hasCivil = activePhases.some(p => normDisc(p.discipline) === 'civil')
      || itemsToClaim.some(it => normDisc(it.discipline) === 'civil');

    setLineItems(prev => {
      // Purge prior machine-generated item lines (and their paired labels) so a
      // re-Apply replaces rather than duplicates them. Manual ad-hoc item lines
      // (phase == null) and every other line type are preserved.
      const next: InvoiceLineItem[] = [];
      for (const li of prev) {
        if (li.line_type === 'item' && li.phase != null) {
          const top = next[next.length - 1];
          if (top && top.line_type === 'phase_label' && top.phase === li.phase
              && sameDiscipline(top.discipline, li.discipline)) {
            next.pop();
          }
          continue;
        }
        next.push(li);
      }
      const phasesToAppend: Array<{
        phaseName: ProgressClaimPhaseContext['phase'];
        discipline: Discipline | null;
        sectionName: string | null;
        pct: number;
        qty: number;
        quotedFee: number;
        amount: number;
      }> = [];

      // For each active phase with a non-zero claim, update the existing
      // phase line in place if found; otherwise queue it to append. The
      // existing non-template content (custom items, etc.) is left alone.
      for (const phase of activePhases) {
        // Match by section_id so multiple Custom phases (all sharing
        // phase: 'Custom') are correctly disambiguated.
        const claim = progressClaims.find(c => c.section_id === phase.section_id);
        if (!claim) continue;
        if (claim.percent_complete <= 0) continue;

        const pct = claim.percent_complete;
        const qty = Math.round(pct) / 100;
        const amount = Math.round(phase.quoted_fee * pct) / 100;
        const labelText = `${phaseDisplayLabel(phase.phase, phase.section_name)} - ${pct}% Complete`;

        const idx = findPhaseLineIdx(next, phase);
        if (idx !== -1) {
          next[idx] = { ...next[idx], quantity: qty, rate: phase.quoted_fee, amount };
          // Refresh the paired phase_label description (immediately above)
          if (idx > 0 && next[idx - 1].line_type === 'phase_label' && next[idx - 1].phase === phase.phase
              && sameDiscipline(next[idx - 1].discipline, phase.discipline)) {
            next[idx - 1] = {
              ...next[idx - 1],
              description: labelText,
            };
          }
        } else {
          phasesToAppend.push({
            phaseName: phase.phase,
            discipline: phase.discipline ?? null,
            sectionName: phase.section_name,
            pct,
            qty,
            quotedFee: phase.quoted_fee,
            amount,
          });
        }
      }

      // Insert new phase pairs *before* any existing LPC / spacer block so
      // the deduction stays at the bottom of the document.
      let lpcIdx = next.findIndex(isLessClaimsLine);
      let insertAt = lpcIdx === -1 ? next.length : lpcIdx;
      if (lpcIdx !== -1 && lpcIdx > 0 && next[lpcIdx - 1].line_type === 'spacer') {
        insertAt = lpcIdx - 1;
      }

      if (phasesToAppend.length > 0) {
        // Group structural before civil so the discipline headings read in
        // order. Within a discipline the activePhases order is preserved.
        phasesToAppend.sort((a, b) =>
          (normDisc(a.discipline) === 'civil' ? 1 : 0) - (normDisc(b.discipline) === 'civil' ? 1 : 0));
        const newLines: InvoiceLineItem[] = [];
        const headerExists = (label: string) =>
          next.some(li => li.line_type === 'header' && li.description.trim() === label)
          || newLines.some(li => li.line_type === 'header' && li.description.trim() === label);
        let lastDiscipline: Discipline | null = null;
        for (const p of phasesToAppend) {
          // When civil scope is on, prefix each discipline group with a header
          // line (once). Structural-only invoices get no headings.
          if (hasCivil && normDisc(p.discipline) !== lastDiscipline) {
            const headerLabel = disciplineLabel(p.discipline);
            if (!headerExists(headerLabel)) {
              newLines.push({
                sort_order: 0,
                line_type: 'header',
                description: headerLabel,
                quantity: null,
                rate: null,
                amount: null,
                team_member_id: null,
                service_rate_id: null,
                indent_level: 0,
                phase: null,
                discipline: p.discipline ?? null,
              });
            }
            lastDiscipline = normDisc(p.discipline);
          }
          const labelText = `${phaseDisplayLabel(p.phaseName, p.sectionName)} - ${p.pct}% Complete`;
          newLines.push({
            sort_order: 0,
            line_type: 'phase_label',
            description: labelText,
            quantity: null,
            rate: null,
            amount: null,
            team_member_id: null,
            service_rate_id: null,
            indent_level: 0,
            phase: p.phaseName,
            discipline: p.discipline ?? null,
          });
          newLines.push({
            sort_order: 0,
            line_type: 'phase',
            description: 'As previously agreed',
            quantity: p.qty,
            rate: p.quotedFee,
            amount: p.amount,
            team_member_id: null,
            service_rate_id: null,
            indent_level: 1,
            phase: p.phaseName,
            discipline: p.discipline ?? null,
          });
        }
        next.splice(insertAt, 0, ...newLines);
        // Re-locate the LPC since we just shifted indices
        lpcIdx = next.findIndex(isLessClaimsLine);
      }

      // Append item-based claims (count × rate). Items were purged above, so
      // every claimed item is re-emitted here as a label + item line pair,
      // grouped by discipline (reusing any header the phase block already added),
      // inserted before the spacer/LPC block.
      if (itemsToClaim.length > 0) {
        const sortedItems = [...itemsToClaim].sort((a, b) =>
          (normDisc(a.discipline) === 'civil' ? 1 : 0) - (normDisc(b.discipline) === 'civil' ? 1 : 0));
        let itemLpc = next.findIndex(isLessClaimsLine);
        let itemInsertAt = itemLpc === -1 ? next.length : itemLpc;
        if (itemLpc !== -1 && itemLpc > 0 && next[itemLpc - 1].line_type === 'spacer') {
          itemInsertAt = itemLpc - 1;
        }
        const itemLines: InvoiceLineItem[] = [];
        const headerExists = (label: string) =>
          next.some(li => li.line_type === 'header' && li.description.trim() === label)
          || itemLines.some(li => li.line_type === 'header' && li.description.trim() === label);
        for (const it of sortedItems) {
          if (hasCivil && !headerExists(disciplineLabel(it.discipline))) {
            itemLines.push({
              sort_order: 0, line_type: 'header', description: disciplineLabel(it.discipline),
              quantity: null, rate: null, amount: null, team_member_id: null,
              service_rate_id: null, indent_level: 0, phase: null, discipline: it.discipline ?? null,
            });
          }
          itemLines.push({
            sort_order: 0, line_type: 'phase_label',
            description: phaseDisplayLabel(it.phase, it.section_name),
            quantity: null, rate: null, amount: null, team_member_id: null,
            service_rate_id: null, indent_level: 0, phase: it.phase, discipline: it.discipline ?? null,
          });
          itemLines.push({
            sort_order: 0, line_type: 'item', description: it.description,
            quantity: it.count, rate: it.rate, amount: Math.round(it.count * (it.rate ?? 0) * 100) / 100,
            team_member_id: null, service_rate_id: null, indent_level: 1,
            phase: it.phase, discipline: it.discipline ?? null,
          });
        }
        next.splice(itemInsertAt, 0, ...itemLines);
        lpcIdx = next.findIndex(isLessClaimsLine);
      }

      // Update or append the LPC line. Append uses the same spacer + LPC
      // pair the seed produces; update preserves the existing position.
      if (totalPriorAmount > 0) {
        if (lpcIdx !== -1) {
          next[lpcIdx] = {
            ...next[lpcIdx],
            line_type: 'prior_claims_deduction' as LineItemType,
            description: 'Less previous claims',
            quantity: 1,
            rate: totalPriorAmount,
            amount: totalPriorAmount,
          };
        } else {
          next.push({
            sort_order: 0,
            line_type: 'spacer',
            description: '',
            quantity: null,
            rate: null,
            amount: null,
            team_member_id: null,
            service_rate_id: null,
            indent_level: 0,
          });
          next.push({
            sort_order: 0,
            line_type: 'prior_claims_deduction',
            description: 'Less previous claims',
            quantity: 1,
            rate: totalPriorAmount,
            amount: totalPriorAmount,
            team_member_id: null,
            service_rate_id: null,
            indent_level: 0,
          });
        }
      }

      // Renumber sort_order to match the new array order
      return next.map((item, i) => ({ ...item, sort_order: i }));
    });

    if (!serviceDescription) {
      setServiceDescription('Engineering services');
    }
  }, [progressContext, activePhases, progressClaims, activeItems, serviceDescription, findPhaseLineIdx, setLineItems, setServiceDescription]);

  // =======================================
  // PAGINATION
  // =======================================
  // The invoice is laid out on real A4 pages so the break the PDF will make is
  // visible while editing. Heights are measured from the live DOM: a row's
  // height depends on its content, not on which page it sits on, so measuring
  // and then re-paginating settles rather than oscillating.

  const previewRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PageLayoutMetrics>({
    capacity: 0, head: 0, thead: 0, addArea: 0, summary: 0, rows: [],
  });
  // Measuring feeds pagination, which changes the DOM, which is measured
  // again. It converges because a row's height depends on its content and not
  // on where it sits. This counter is a backstop: if some future change breaks
  // that, the layout freezes at the last measurement instead of looping.
  const measurePassesRef = useRef(0);
  const MAX_MEASURE_PASSES = 12;
  useEffect(() => { measurePassesRef.current = 0; }, [lineItems]);

  const measureLayout = useCallback(() => {
    const root = previewRef.current;
    const pageEl = root?.querySelector('.invoice-preview__page') as HTMLElement | null;
    if (!root || !pageEl) return;
    const cs = window.getComputedStyle(pageEl);
    const height = (sel: string) => {
      const el = root.querySelector(sel) as HTMLElement | null;
      return el ? el.getBoundingClientRect().height : 0;
    };
    const next: PageLayoutMetrics = {
      capacity: pageEl.getBoundingClientRect().height
        - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom),
      head: height('.invoice-preview__head'),
      thead: height('.line-items-table thead'),
      addArea: height('.line-items-add-area'),
      summary: height('.invoice-preview__summary'),
      // Content height only. A row's rendered height also carries whatever
      // padding-top the last pagination gave it - a blank line, or the air
      // under the header rule when it happened to land first on a page - and
      // feeding that back in would make a row's height depend on where it
      // sits. The pagination re-adds those below, from the same constants the
      // stylesheet and the PDF use.
      rows: Array.from(root.querySelectorAll('.line-item-row')).map(el => {
        const row = el as HTMLElement;
        const cell = row.querySelector('td');
        const padTop = cell ? parseFloat(window.getComputedStyle(cell).paddingTop) : 0;
        return row.getBoundingClientRect().height - (isNaN(padTop) ? 0 : padTop);
      }),
    };
    setLayout(prev => {
      const unchanged =
        sameHeight(prev.capacity, next.capacity) && sameHeight(prev.head, next.head) &&
        sameHeight(prev.thead, next.thead) && sameHeight(prev.addArea, next.addArea) &&
        sameHeight(prev.summary, next.summary) &&
        prev.rows.length === next.rows.length &&
        prev.rows.every((v, i) => sameHeight(v, next.rows[i]));
      if (unchanged) return prev;
      if (measurePassesRef.current >= MAX_MEASURE_PASSES) {
        console.warn('Invoice page measurement did not settle; keeping the last layout.');
        return prev;
      }
      measurePassesRef.current += 1;
      return next;
    });
  }, []);

  // Runs after every render; the equality check above stops it looping.
  useLayoutEffect(() => { measureLayout(); });

  // Put the caret in a just-inserted line. Runs after the measure above, and
  // keeps doing so for a moment: pagination can move the new row to the next
  // page, which remounts it and drops focus. A cell of that row already
  // having focus (the user has tabbed on) is left alone.
  useLayoutEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    if (performance.now() > pending.until) {
      pendingFocusRef.current = null;
      return;
    }
    const row = document.querySelectorAll('.line-item-row')[pending.index];
    if (!row || row.contains(document.activeElement)) return;
    (row.querySelector('.description-input') as HTMLElement | null)?.focus();
  });

  // Tinos arrives after the first paint, and every height changes when it does.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready.then(() => { if (!cancelled) measureLayout(); });
    return () => { cancelled = true; };
  }, [measureLayout]);

  const invoicePagesRef = useRef<InvoicePage[]>([]);
  const invoicePages = useMemo<InvoicePage[]>(() => {
    const count = lineItems.length;
    const singlePage: InvoicePage[] = [{
      rows: Array.from({ length: count }, (_, i) => i),
      showAddArea: true, showSummary: true,
    }];
    // Before the first measurement - and in the render where the row count has
    // moved but the heights have not been re-read - keep everything on one
    // page. That is also the state the measurement pass reads.
    if (!layout.capacity || layout.rows.length !== count) return singlePage;

    const pages: InvoicePage[] = [];
    let current: InvoicePage = { rows: [], showAddArea: false, showSummary: false };
    let used = layout.head;
    const startNewPage = () => {
      pages.push(current);
      current = { rows: [], showAddArea: false, showSummary: false };
      used = 0;
    };

    for (let i = 0; i < count; i++) {
      const content = layout.rows[i] ?? 0;
      // A blank line above, unless this row opens a page - the stylesheet
      // suppresses it there, and so does the PDF. Measuring content height and
      // adding the gap here (rather than measuring the rendered height) keeps a
      // row's cost independent of which page it lands on, which is what stops
      // the measure/paginate loop oscillating at a page boundary.
      const wantsGap = hasGapBetween(lineItems[i - 1], lineItems[i]);
      const opensPage = current.rows.length === 0;
      // A page that carries rows repeats the column header, and its first row
      // takes the air under that header's rule instead of any gap.
      const needed = opensPage
        ? layout.thead + LINE_ITEM_FIRST_ROW_PAD_PX + content
        : (wantsGap ? LINE_ITEM_LINE_PX : 0) + content;

      if (used + needed > layout.capacity && !opensPage) {
        startNewPage();
        used += layout.thead + LINE_ITEM_FIRST_ROW_PAD_PX + content;
      } else {
        used += needed;
      }
      current.rows.push(i);
    }

    // The click-to-add zone stays with the last row
    if (used + layout.addArea > layout.capacity && current.rows.length > 0) startNewPage();
    current.showAddArea = true;
    used += layout.addArea;

    // Payment details and totals are one row, so they move as one block
    if (used + layout.summary > layout.capacity && current.rows.length > 0) startNewPage();
    current.showSummary = true;
    pages.push(current);
    return pages;
  }, [lineItems, layout]);
  invoicePagesRef.current = invoicePages;

  if (loading) {
    return (
      <div className="invoice-editor">
        <div className="invoice-editor__loading">Loading invoice...</div>
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="invoice-editor">
        <div className="invoice-editor__error">
          <h3>Error</h3>
          <p>{error || 'Invoice not found'}</p>
          <button onClick={handleClose}>Back to Invoices</button>
        </div>
      </div>
    );
  }

  const { invoice, project, client, contact, stage } = invoiceData;

  const fullProjectDesc = stage?.stage_description
    ? `${project?.description || ''} – ${stage.stage_description}`
    : project?.description || '';

  const fullSiteAddress = formatSiteAddress(project);
  const showSiteAddress = isProgressClaimProjectType(project?.types);

  // Client billing address preview — mirrors the PDF. The care-of override here
  // is the in-editor (unsaved) value so the preview updates live as you type.
  const previewCareOf = resolveCareOf({ care_of_override: careOfOverride }, stage, project);
  const clientAddress = client ? buildClientAddressLines(client, previewCareOf) : null;

  // The page is assembled from these sections. The pagination below places
  // them across as many A4 pages as the content needs, so the editor shows
  // the same page breaks the PDF will produce.
  const headSection = (
    <div className="invoice-preview__head">
        <div className="invoice-preview__header-band">
          <img src="/terpkosLogo.png" alt="Terpkos Engineering" className="invoice-preview__logo" />
          <div className="invoice-preview__header-title">TAX INVOICE</div>
        </div>

        {/* Two-column: Client left, invoice details right */}
        <div className="invoice-preview__meta">
          <div className="invoice-preview__meta-left">
            {clientAddress && (
              <div className="invoice-preview__client-section">
                {clientAddress.headerLines.map((line, i) => (
                  <span key={`hdr-${i}`} className="client-header-line">{line}</span>
                ))}
                <span className="client-company">{clientAddress.topName}</span>
                {clientAddress.careOfClientName && (
                  <span className="client-care-of">c/- {clientAddress.careOfClientName}</span>
                )}
                {clientAddress.addressLines.map((line, i) => (
                  <span key={`addr-${i}`} className="client-address">{line}</span>
                ))}
                {clientAddress.cityLine && (
                  <span className="client-city">{clientAddress.cityLine}</span>
                )}
              </div>
            )}
          </div>
          <div className="invoice-preview__meta-right">
            <div className="meta-row">
              <span className="meta-label">Invoice No:</span>
              <span className="meta-value">{invoice.invoice_number}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Date:</span>
              <span className="meta-value">{formatInvoiceDate(invoiceDate || invoice.invoice_date)}</span>
            </div>
            {project && (
              <div className="meta-row">
                <span className="meta-label">Job No:</span>
                <span className="meta-value">{project.job_number}{stage ? `.${stage.stage_id}` : ''}</span>
              </div>
            )}
          </div>
        </div>

        {/* ATTENTION */}
        {(contactOverride || contact) && (
          <div className="invoice-preview__attention-row">
            <span className="attention-label">ATTENTION:</span>
            <span>
              {contactOverride
                ? contactOverride
                : `${contact!.title ? contact!.title + ' ' : ''}${contact!.first_name} ${contact!.last_name}`}
            </span>
          </div>
        )}

        {/* PROJECT: two blank lines below ATTENTION, as in the letter template */}
        {(projectDescriptionOverride || project) && (
          <div className="invoice-preview__project-row">
            <span className="project-label">PROJECT:</span>
            <span className="project-content">{projectDescriptionOverride || fullProjectDesc}</span>
          </div>
        )}

        {showSiteAddress && (siteAddressOverride || fullSiteAddress) && (
          <div className="invoice-preview__address-row">
            {siteAddressOverride || fullSiteAddress}
          </div>
        )}

        {/* Service Description (editable) */}
        <div className="invoice-preview__service-desc">
          <input
            type="text"
            value={serviceDescription}
            onChange={(e) => setServiceDescription(e.target.value)}
            placeholder="Service description (e.g. Retaining Wall Inspections)"
            className="inline-edit"
          />
        </div>

        {/* PO (editable) */}
        <div className="invoice-preview__po-row">
          <input
            type="text"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="PO: (purchase order number)"
            className="inline-edit"
          />
        </div>

        {/* Claim Title (structured: auto "Progress Claim N" or toggle to "Final Claim") */}
        <div className="invoice-preview__claim-title">
          <span className="claim-title-display">{claimTitle}</span>
          <button
            type="button"
            className="claim-title-toggle"
            onClick={() => {
              const isFinal = claimTitle === FINAL_CLAIM_TITLE;
              const position = invoiceData?.invoice_position ?? 1;
              setClaimTitle(isFinal ? `Progress Claim ${position}` : FINAL_CLAIM_TITLE);
            }}
          >
            {claimTitle === FINAL_CLAIM_TITLE
              ? `Mark as Progress Claim ${invoiceData?.invoice_position ?? 1}`
              : 'Mark as Final Claim'}
          </button>
        </div>
    </div>
  );

  const tableHead = (
    <thead>
      <tr>
        <th className="col-description">Description</th>
        <th className="col-qty">Qty</th>
        <th className="col-rate">Rate</th>
        <th className="col-amount">Amount</th>
        <th className="col-actions"></th>
      </tr>
    </thead>
  );

  const renderLineItem = (index: number) => {
    const item = lineItems[index];
    if (!item) return null;
    return (
      <LineItemEditor
        key={`${item.id || 'new'}-${index}`}
        item={item}
        index={index}
        // A blank line above exists only when this line and the one before it
        // agree; the first line on a page never gets one (CSS handles that).
        gapAbove={hasGapBetween(lineItems[index - 1], item)}
        isActive={activeLineIndex === index}
        onChange={(updates) => updateLineItem(index, updates)}
        onRemove={() => { removeLineItem(index); setActiveLineIndex(null); }}
        onMoveUp={index > 0 ? () => moveLineItem(index, index - 1) : undefined}
        onMoveDown={index < lineItems.length - 1 ? () => moveLineItem(index, index + 1) : undefined}
        onInsertAfter={(type) => insertLineItemAfter(index, type)}
        isSlashMenuOpen={slashMenu.isOpen}
        onSlashCommand={(position) => {
          setSlashMenu({
            isOpen: true,
            position,
            searchQuery: '',
            targetIndex: index,
          });
        }}
        onEnterOnEmpty={() => {
          updateLineItem(index, { line_type: 'spacer', description: '' });
          insertLineItemAfter(index, 'item');
        }}
        onBackspaceOnEmpty={() => {
          removeLineItem(index);
          setTimeout(() => {
            const rows = document.querySelectorAll('.line-item-row');
            const prevRow = rows[Math.max(0, index - 1)];
            const el = prevRow?.querySelector('.description-input') as HTMLElement;
            if (el) {
              el.focus();
              if (el instanceof HTMLInputElement) {
                el.setSelectionRange(el.value.length, el.value.length);
              } else {
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(el);
                range.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(range);
              }
            }
          }, 50);
        }}
        onRowFocus={handleRowFocus}
        lessClaimsRecomputeTarget={progressContext?.prior_invoiced_total ?? null}
        readOnly={isLocked}
      />
    );
  };

  const addAreaSection = (
    <div
      className={`line-items-add-area${lineItems.length === 0 ? ' line-items-add-area--empty' : ''}`}
      onClick={() => {
        const last = lineItems[lineItems.length - 1];
        if (last && !last.description.trim()) {
          const rows = document.querySelectorAll('.line-item-row');
          const el = rows[rows.length - 1]?.querySelector('.description-input') as HTMLElement | null;
          el?.focus();
          return;
        }
        insertLineItemAfter(lineItems.length - 1, 'item');
      }}
      title="Click to add a line item"
    >
      {lineItems.length === 0 && (
        <span className="line-items-add-area__hint">
          No line items yet. Click here or use the sidebar to add items.
        </span>
      )}
    </div>
  );

  // Payment details and totals share one row under the closing rule. Stacking
  // them cost about 100px of page height, which is enough to push the block —
  // and with it a whole page — past the bottom margin on a full invoice.
  const summarySection = (
    <div className="invoice-preview__summary">
      <div className="invoice-preview__bank">
        <span>Please pay <strong>TERPKOS ENGINEERING</strong> on invoice – no statement issued</span>
        <span>E.F.T. your payment to Commonwealth Bank Australia</span>
        <span>BSB: 066 000</span>
        <span>Account No: 1100 0312</span>
        <span>Reference: {invoice.invoice_number}</span>
      </div>
      <div className="invoice-preview__totals">
        <div className="totals-row">
          <span className="totals-label">Our Fee (ex GST):</span>
          <span className="totals-value">{formatCurrency(totals.ourFee)}</span>
        </div>
        {totals.disbursements > 0 && (
          <>
            <div className="totals-row">
              <span className="totals-label">Disbursements (ex GST):</span>
              <span className="totals-value">{formatCurrency(totals.disbursements)}</span>
            </div>
            <div className="totals-row">
              <span className="totals-label">Subtotal:</span>
              <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
            </div>
          </>
        )}
        <div className="totals-row">
          <span className="totals-label">GST (10%):</span>
          <span className="totals-value">{formatCurrency(totals.gst)}</span>
        </div>
        <div className="totals-row totals-row--total">
          <span className="totals-label">TOTAL (inc GST):</span>
          <span className="totals-value">{formatCurrency(totals.total)}</span>
        </div>
        {hasBundledChildren && (
          <div className="totals-row totals-row--allocation" title="Editor only — not printed on the PDF">
            <span className="totals-label">This period allocation (inc GST):</span>
            <span className="totals-value">{formatCurrency(allocationTotals.total)}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Right-panel tabs. Progress only appears when the stage actually has a fee
  // plan to claim against, so the segmented control carries three or four.
  const rightTabs: Array<{ id: SidebarTab; label: string; icon: React.ReactNode }> = [
    { id: 'templates', label: 'Templates', icon: ICON_TEMPLATES },
    { id: 'items', label: 'Items', icon: ICON_ITEMS },
    { id: 'fields', label: 'Fields', icon: ICON_FIELDS },
    ...(progressContext && (activePhases.length > 0 || activeItems.length > 0)
      ? [{ id: 'progress' as SidebarTab, label: 'Progress', icon: ICON_PROGRESS }]
      : []),
  ];

  return (
    // INVOICE_PAGE_VARS is a static token object from invoiceLayout.ts, the
    // single source of the page geometry the stylesheet and the PDF share.
    <div className="invoice-editor" style={INVOICE_PAGE_VARS as React.CSSProperties}>
      {/* Toolbar */}
      <div className="invoice-editor__toolbar">
        <div className="toolbar-left">
          <div className="invoice-identity">
            <span className="invoice-identity__number">
              TAX INVOICE {invoice.invoice_number}
            </span>
            <span className="invoice-identity__title">
              {project
                ? `Job ${project.job_number}${stage ? `.${stage.stage_id}` : ''} — ${stage ? stage.stage_description : project.description}`
                : 'Tax Invoice'}
            </span>
          </div>
        </div>
        <div className="toolbar-right">
          <span
            className={`save-indicator ${saveStatus === 'idle' && hasUnsavedChanges ? 'dirty' : saveStatus}`}
            aria-live="polite"
          >
            <span className="save-indicator__dot" aria-hidden="true" />
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && 'Save failed'}
            {saveStatus === 'idle' && (hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved')}
          </span>
          <div className="status-select" ref={statusMenuRef}>
            <button
              type="button"
              className={`toolbar-btn status-select__toggle status-${invoice.status.toLowerCase()}`}
              onClick={() => setStatusMenuOpen(v => !v)}
              // A bundled invoice's status cascades from its parent
              // (cascade_invoice_status_to_bundled); changing it here would let
              // the child diverge. Matches the InvoiceDetailModal, which shows
              // the status as "(follows parent)" for bundled rows.
              disabled={saving || statusChanging || isDeletingTaxInvoice || isLockedByBundling}
              title={isLockedByBundling ? 'Status follows the parent invoice' : 'Change status'}
              aria-haspopup="menu"
              aria-expanded={statusMenuOpen}
            >
              <span>{statusChanging ? 'Updating…' : invoice.status}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {statusMenuOpen && !isLockedByBundling && (
              <div className="status-select__menu" role="menu">
                {INVOICE_STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={opt.value === invoice.status}
                    className={`status-select__menu-item${opt.value === invoice.status ? ' status-select__menu-item--active' : ''}`}
                    onClick={() => handleStatusChange(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="toolbar-divider" aria-hidden="true" />
          <div className="save-split-button" ref={saveMenuRef}>
            <button
              className={`toolbar-btn btn-secondary save-split-button__main${lineItems.length > 0 && !isLocked ? '' : ' save-split-button__main--solo'}`}
              onClick={() => handleSave()}
              disabled={saving}
            >
              Save
            </button>
            {lineItems.length > 0 && !isLocked && (
              <button
                type="button"
                className="toolbar-btn btn-secondary save-split-button__chevron"
                onClick={() => setSaveMenuOpen(v => !v)}
                disabled={saving || isDeletingTaxInvoice}
                title="More actions"
                aria-label="More actions"
                aria-expanded={saveMenuOpen}
                aria-haspopup="menu"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
            {saveMenuOpen && (
              <div className="save-split-button__menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="save-split-button__menu-item save-split-button__menu-item--danger"
                  onClick={() => {
                    setSaveMenuOpen(false);
                    setShowDeleteTaxInvoiceConfirm(true);
                  }}
                >
                  Delete tax invoice
                </button>
              </div>
            )}
          </div>
          <button
            className="toolbar-btn btn-primary"
            onClick={handleGeneratePdf}
            disabled={isGeneratingPdf || saving}
          >
            {isGeneratingPdf ? 'Generating...' : 'Generate PDF'}
          </button>
          {pdfError && (
            <span className="pdf-error" role="alert" title={pdfError}>{pdfError}</span>
          )}
          <button className="close-button" onClick={handleClose} title="Close (Esc)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="invoice-editor__content">
        {/* Left Sidebar — Time allocations + prior invoices */}
        <LeftSidebar
          invoiceId={invoiceId}
          currentInvoice={invoiceData ? {
            id: invoiceData.invoice.id,
            invoice_number: invoiceData.invoice.invoice_number,
            invoice_date: invoiceDate || invoiceData.invoice.invoice_date,
            status: invoiceData.invoice.status,
            // LHS panel shows ex-GST amounts. When there are no editor line
            // items (modal-only invoices — both historical Sent entries and
            // freshly-created Drafts that haven't been built yet), the DB's
            // stored `our_fee` is the canonical value. Once line items
            // exist, derive from the live editor totals; bundled parents
            // show their period allocation.
            total_amount: lineItems.length === 0
              ? invoiceData.invoice.total_amount
              : (hasBundledChildren ? allocationTotals.total : totals.total),
            our_fee: lineItems.length === 0
              ? invoiceData.invoice.our_fee
              : (hasBundledChildren ? allocationTotals.subtotal : totals.subtotal),
          } : null}
          onBundlingChanged={() => { void fetchInvoiceDetail(); }}
          activeTab={lhsActiveTab}
          onActiveTabChange={setLhsActiveTab}
        />

        {/* Main Preview Area */}
        <div className="invoice-editor__main">
          {invoiceData?.bundling_target && (
            <div className="invoice-editor__bundled-banner">
              <div className="invoice-editor__bundled-banner__text">
                <strong>Bundled into {invoiceData.bundling_target.invoice_number}.</strong>{' '}
                Edits to the document happen there. Status follows {invoiceData.bundling_target.invoice_number}.
              </div>
              <div className="invoice-editor__bundled-banner__actions">
                <button
                  type="button"
                  className="invoice-editor__bundled-banner__btn"
                  onClick={() => navigate(`/invoices/${invoiceData.bundling_target!.id}`, { state: location.state })}
                >
                  Open {invoiceData.bundling_target.invoice_number}
                </button>
                <button
                  type="button"
                  className="invoice-editor__bundled-banner__btn invoice-editor__bundled-banner__btn--secondary"
                  onClick={async () => {
                    const { error } = await supabase.rpc('set_invoice_bundling', {
                      p_invoice_id: invoiceData.invoice.id,
                      p_bundled_in_invoice_id: null,
                    });
                    if (error) {
                      console.error('Error unbundling:', error);
                      return;
                    }
                    void fetchInvoiceDetail();
                  }}
                >
                  Unbundle
                </button>
              </div>
            </div>
          )}
          {isLockedBySent && !isLockedByBundling && (
            <div className="invoice-editor__bundled-banner">
              <div className="invoice-editor__bundled-banner__text">
                <strong>This invoice is {invoiceData?.invoice.status}.</strong>{' '}
                The tax invoice is locked. Revert status to <strong>Draft</strong> to edit.
              </div>
            </div>
          )}
          <div ref={previewRef} className={`invoice-preview${isLocked ? ' invoice-preview--bundled' : ''}`} onMouseDown={(e) => {
              const target = e.target as Element;
              if (!target.closest('.line-item-row') && !target.closest('.invoice-line-toolbar')) {
                setActiveLineIndex(null);
              }
            }}>
            {invoicePages.map((page, pageIndex) => (
              <div className="invoice-preview__page" key={pageIndex}>
                {pageIndex === 0 && headSection}

                {/* Line Items. The column header repeats on every page that
                    carries rows, matching the PDF. */}
                {page.rows.length > 0 && (
                  <div className="invoice-preview__line-items">
                    <table className="line-items-table">
                      {tableHead}
                      <tbody>
                        {page.rows.map(renderLineItem)}
                      </tbody>
                    </table>
                    {page.showAddArea && addAreaSection}
                  </div>
                )}
                {page.rows.length === 0 && page.showAddArea && (
                  <div className="invoice-preview__line-items">
                    {addAreaSection}
                  </div>
                )}

                {page.showSummary && summarySection}

                <div className="invoice-preview__footer">
                  Terpkos Engineering Pty Ltd ACN 126 167 324 as trustee for the TEG Trust ABN 76 383 316 132
                  {invoicePages.length > 1 && (
                    <span className="page-number">Page {pageIndex + 1} of {invoicePages.length}</span>
                  )}
                </div>
              </div>
            ))}
            {/* Line Item Toolbar */}
            <InvoiceLineToolbar
              visible={activeLineIndex !== null}
              position={toolbarPosition}
              currentType={activeLineIndex !== null ? (lineItems[activeLineIndex]?.line_type || 'item') : 'item'}
              currentTeamMemberId={activeLineIndex !== null ? (lineItems[activeLineIndex]?.team_member_id || null) : null}
              currentIndentLevel={activeLineIndex !== null ? (lineItems[activeLineIndex]?.indent_level ?? 0) : 0}
              teamMembers={teamMembers}
              getChargeRate={getChargeRate}
              onChangeType={handleToolbarTypeChange}
              onChangeTeamMember={handleToolbarTeamMemberChange}
              onIndent={handleToolbarIndent}
              currentSpacing={
                activeLineIndex !== null && lineItems[activeLineIndex]
                  ? {
                      above: hasGapBetween(lineItems[activeLineIndex - 1], lineItems[activeLineIndex]),
                      below: !!lineItems[activeLineIndex + 1]
                        && hasGapBetween(lineItems[activeLineIndex], lineItems[activeLineIndex + 1]),
                    }
                  : { above: false, below: false }
              }
              onToggleSpacing={handleToolbarToggleSpacing}
              onInsertAbove={() => { if (activeLineIndex !== null) insertLineItemAfter(activeLineIndex - 1, 'item'); }}
              onInsertBelow={() => { if (activeLineIndex !== null) insertLineItemAfter(activeLineIndex, 'item'); }}
              onDelete={handleToolbarDelete}
            />

            {/* Slash Command Menu */}
            <InvoiceSlashMenu
              isOpen={slashMenu.isOpen}
              onClose={closeSlashMenu}
              onSelect={handleSlashSelect}
              onChangeType={handleSlashChangeType}
              onDelete={handleSlashDelete}
              position={slashMenu.position}
              searchQuery={slashMenu.searchQuery}
              currentType={slashMenu.targetIndex !== null ? lineItems[slashMenu.targetIndex]?.line_type || 'item' : 'item'}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className={`invoice-editor__sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
              aria-label={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
                <line x1="10" y1="2.5" x2="10" y2="13.5" />
              </svg>
            </button>
            {!sidebarCollapsed && !isLocked && (
              <div className={`sidebar-tabs${rightTabs.length > 2 ? ' sidebar-tabs--dense' : ''}`} role="tablist">
                {rightTabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    title={tab.label}
                    className={`sidebar-tab${activeTab === tab.id ? ' sidebar-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.icon}
                    <span className="sidebar-tab__label">{tab.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {sidebarCollapsed && !isLocked && (
            <div className="sidebar-rail">
              {rightTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`rail-btn ${activeTab === tab.id ? 'active' : ''}`}
                  title={tab.label}
                  aria-label={tab.label}
                  onClick={() => { setActiveTab(tab.id); setSidebarCollapsed(false); }}
                >
                  {tab.icon}
                </button>
              ))}
            </div>
          )}

          {!sidebarCollapsed && isLocked && (
            <div className="sidebar-content">
              <div className="sidebar-section">
                <p className="sidebar-section__hint">
                  {isLockedBySent && !isLockedByBundling && (
                    <>This invoice is <strong>{invoiceData?.invoice.status}</strong>. Revert status to <strong>Draft</strong> in the detail modal to add or change items.</>
                  )}
                  {isLockedByBundling && (
                    <>This invoice is bundled into another. Unbundle it first to add or change items.</>
                  )}
                </p>
              </div>
            </div>
          )}

          {!sidebarCollapsed && !isLocked && (
            <div className="sidebar-content">
              {activeTab === 'templates' && (
                <>
                  {rwcTemplateContext && (
                    <div className="sidebar-section">
                      <h3 className="sidebar-section__title">RWC Templates</h3>
                      <p className="sidebar-section__hint sidebar-section__hint--subtext">
                        Templates add to existing items. Rates from project settings.
                      </p>
                      <div className="sidebar-section__templates">
                        <button
                          className="sidebar-btn sidebar-btn--template"
                          onClick={() => applyRWCTemplate('inspections')}
                        >
                          <span className="template-icon" aria-hidden="true">{ICON_TEMPLATE_CARD}</span>
                          <span className="template-text">
                            <span className="template-name">Pre-start + Inspections</span>
                            <span className="template-hint">
                              {rwcTemplateContext.hasStartup ? 'Startup' : ''}
                              {rwcTemplateContext.hasStartup && rwcTemplateContext.numInspections > 0 ? ' + ' : ''}
                              {rwcTemplateContext.numInspections > 0 ? `${rwcTemplateContext.numInspections} inspection${rwcTemplateContext.numInspections !== 1 ? 's' : ''}` : ''}
                            </span>
                          </span>
                        </button>
                        {rwcTemplateContext.hasQA && (
                          <button
                            className="sidebar-btn sidebar-btn--template"
                            onClick={() => applyRWCTemplate('qa')}
                          >
                            <span className="template-icon" aria-hidden="true">{ICON_TEMPLATE_CARD}</span>
                            <span className="template-text">
                              <span className="template-name">QA Review & As Constructed</span>
                              <span className="template-hint">
                                {rwcTemplateContext.qaRate ? formatCurrency(rwcTemplateContext.qaRate.rate) : 'No rate set'}
                              </span>
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {rwdTemplateContext && (
                    <div className="sidebar-section">
                      <h3 className="sidebar-section__title">RWD Templates</h3>
                      <p className="sidebar-section__hint sidebar-section__hint--subtext">
                        Templates add drawings as line items with the selected rate.
                      </p>
                      <div className="sidebar-section__templates">
                        <button
                          className="sidebar-btn sidebar-btn--template"
                          onClick={() => applyRWDTemplate('first_issue')}
                        >
                          <span className="template-icon" aria-hidden="true">{ICON_TEMPLATE_CARD}</span>
                          <span className="template-text">
                            <span className="template-name">First Issue</span>
                            <span className="template-hint">
                              {rwdTemplateContext.drawings.length} drawing{rwdTemplateContext.drawings.length !== 1 ? 's' : ''} · {rwdTemplateContext.firstIssueRate ? formatCurrency(rwdTemplateContext.firstIssueRate.rate) : 'No rate'}
                            </span>
                          </span>
                        </button>
                        <button
                          className="sidebar-btn sidebar-btn--template"
                          onClick={() => applyRWDTemplate('revision')}
                        >
                          <span className="template-icon" aria-hidden="true">{ICON_TEMPLATE_CARD}</span>
                          <span className="template-text">
                            <span className="template-name">Revision</span>
                            <span className="template-hint">
                              {rwdTemplateContext.drawings.length} drawing{rwdTemplateContext.drawings.length !== 1 ? 's' : ''} · {rwdTemplateContext.revisionRate ? formatCurrency(rwdTemplateContext.revisionRate.rate) : 'No rate'}
                            </span>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}

                  {!rwcTemplateContext && !rwdTemplateContext && (() => {
                    const projType = invoiceData.project?.types;
                    if (!isProgressClaimProjectType(projType)) {
                      return (
                        <p className="sidebar-section__hint">
                          No templates available for {projType ?? 'this'} projects.
                        </p>
                      );
                    }
                    if (!progressContext) {
                      return <p className="sidebar-section__hint">Loading fee plan…</p>;
                    }
                    if (!progressContext.accepted_fee_id) {
                      return (
                        <div className="sidebar-section">
                          <p className="sidebar-section__hint sidebar-section__hint--subtext">
                            This project has no fee plan yet.
                          </p>
                          <a
                            href="/fees"
                            className="sidebar-btn sidebar-btn--primary"
                            style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}
                          >
                            Set up fee plan in Fees register
                          </a>
                        </div>
                      );
                    }
                    if (activePhases.length === 0 && activeItems.length === 0) {
                      return (
                        <p className="sidebar-section__hint">
                          The fee plan has no phases or items with values. Add a quoted fee in the Fees register.
                        </p>
                      );
                    }
                    return (
                      <div className="sidebar-section">
                        <p className="sidebar-section__hint sidebar-section__hint--subtext">
                          Use the Progress tab to add progress claim line items.
                        </p>
                        <button
                          type="button"
                          className="sidebar-btn sidebar-btn--primary"
                          onClick={() => setActiveTab('progress')}
                        >
                          Open Progress tab
                        </button>
                      </div>
                    );
                  })()}
                </>
              )}

              {activeTab === 'items' && (
                <>
                  {/* Quick Add Section */}
                  <div className="sidebar-section">
                    <h3 className="sidebar-section__title">Add Line Items</h3>
                    <div className="sidebar-section__buttons">
                      <button className="sidebar-btn" onClick={() => addLineItem('item')}>
                        {ICON_PLUS}
                        Fee Item
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('disbursement')}>
                        {ICON_PLUS}
                        Disbursement
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('header')}>
                        {ICON_PLUS}
                        Header
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('text')}>
                        {ICON_PLUS}
                        Text Line
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('bullet')}>
                        {ICON_PLUS}
                        Bullet Point
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('drawing')}>
                        {ICON_PLUS}
                        Drawing
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('deduction')}>
                        {ICON_PLUS}
                        Deduction
                      </button>
                      <button className="sidebar-btn" onClick={() => addLineItem('spacer')}>
                        {ICON_PLUS}
                        Spacer
                      </button>
                    </div>
                  </div>

                  {/* Service Rates */}
                  {applicableRates.length > 0 && (
                    <div className="sidebar-section">
                      <h3 className="sidebar-section__title">
                        {invoiceData.project?.types} Rates
                      </h3>
                      <div className="sidebar-section__rates">
                        {applicableRates.map(rate => (
                          <button
                            key={rate.id}
                            className="rate-btn"
                            onClick={() => addFromServiceRate(rate)}
                          >
                            <span className="rate-btn__desc">{rate.description}</span>
                            <span className="rate-btn__amount">{formatCurrency(rate.rate)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hourly Charges */}
                  {engineers.length > 0 && (
                    <div className="sidebar-section">
                      <h3 className="sidebar-section__title">Hourly Charges</h3>
                      <div className="sidebar-section__rates">
                        {engineers.map(eng => {
                          const rate = getChargeRate(eng.id);
                          return (
                            <button
                              key={eng.id}
                              className="rate-btn"
                              onClick={() => addHourlyCharge(eng.id)}
                            >
                              <span className="rate-btn__desc">{eng.first_name} {eng.last_name}</span>
                              <span className="rate-btn__amount">
                                {rate ? `${formatCurrency(rate)}/hr` : 'No rate'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="sidebar-section sidebar-section--summary">
                    <h3 className="sidebar-section__title">Summary</h3>
                    <div className="summary-grid">
                      <span>Fee Items</span>
                      <span>{formatCurrency(totals.ourFee)}</span>
                      <span>Disbursements</span>
                      <span>{formatCurrency(totals.disbursements)}</span>
                      <span>GST</span>
                      <span>{formatCurrency(totals.gst)}</span>
                      <span className="summary-total">Total</span>
                      <span className="summary-total">{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'fields' && (
                <>
                <div className="sidebar-section">
                  <h3 className="sidebar-section__title">Project Fields</h3>
                  <p className="sidebar-section__hint sidebar-section__hint--subtext">
                    Pulled from the project. Click refresh to pick up changes.
                  </p>
                  <div className="sidebar-fields">
                    {(() => {
                      const cityLine = [client?.city, abbreviateState(client?.state ?? null), client?.postcode]
                        .filter(Boolean)
                        .join(', ');
                      const addressParts = [client?.street_address, cityLine].filter(Boolean);
                      const projectStagePo = stage?.po ?? project?.po ?? null;
                      const fields: Array<{
                        label: string;
                        value: string | null | undefined;
                        onRefresh?: () => unknown;
                      }> = [
                        { label: 'Client', value: client?.company_name, onRefresh: refreshProjectData },
                        { label: 'Client Address', value: addressParts.length > 0 ? addressParts.join(' — ') : null, onRefresh: refreshProjectData },
                        { label: 'Date', value: invoice.invoice_date ? formatInvoiceDate(invoice.invoice_date) : null },
                        { label: 'Job Number', value: project ? `${project.job_number}${stage ? `.${stage.stage_id}` : ''}` : null },
                        { label: 'Contact', value: contact ? `${contact.title ? contact.title + ' ' : ''}${contact.first_name} ${contact.last_name}` : null, onRefresh: refreshProjectData },
                        { label: 'Project Description', value: fullProjectDesc || null, onRefresh: refreshProjectData },
                        ...(showSiteAddress ? [{ label: 'Address', value: fullSiteAddress || null, onRefresh: refreshProjectData }] : []),
                        { label: 'PO Number', value: projectStagePo, onRefresh: refreshPoFromProject },
                      ];
                      return fields.map((f) => (
                        <div key={f.label} className="sidebar-field">
                          <div className="sidebar-field__header">
                            <span className="sidebar-field__label">{f.label}</span>
                            {f.onRefresh && (
                              <button
                                type="button"
                                className="sidebar-field__refresh"
                                onClick={f.onRefresh}
                                title={`Refresh ${f.label.toLowerCase()} from project`}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="23 4 23 10 17 10" />
                                  <polyline points="1 20 1 14 7 14" />
                                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <span className={`sidebar-field__value${f.value ? '' : ' sidebar-field__value--empty'}`}>
                            {f.value || '—'}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3 className="sidebar-section__title">Overrides</h3>
                  <p className="sidebar-section__hint sidebar-section__hint--subtext">
                    Replace project values with custom text for this invoice only.
                  </p>
                  <div className="sidebar-overrides">
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Sent</span>
                      <input
                        type="date"
                        value={invoiceDate}
                        onChange={(e) => handleInvoiceDateChange(e.target.value)}
                        className="sidebar-override__input"
                      />
                    </label>
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Recorded</span>
                      <input
                        type="date"
                        value={recordedDate}
                        onChange={(e) => setRecordedDate(e.target.value)}
                        className="sidebar-override__input"
                        title="Date this invoice is recorded against for budgeting. Defaults to the invoice date."
                      />
                    </label>
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Contact</span>
                      <input
                        type="text"
                        value={contactOverride}
                        onChange={(e) => setContactOverride(e.target.value)}
                        placeholder="Leave blank to use the job's contact"
                        className="sidebar-override__input"
                      />
                    </label>
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Care of</span>
                      <input
                        type="text"
                        value={careOfOverride}
                        onChange={(e) => setCareOfOverride(e.target.value)}
                        placeholder="Leave blank to use project/stage care-of"
                        className="sidebar-override__input"
                      />
                    </label>
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Project Description</span>
                      <textarea
                        value={projectDescriptionOverride}
                        onChange={(e) => setProjectDescriptionOverride(e.target.value)}
                        placeholder="Leave blank to use project description"
                        className="sidebar-override__input sidebar-override__input--textarea"
                        rows={2}
                      />
                    </label>
                    {showSiteAddress && (
                      <label className="sidebar-override">
                        <span className="sidebar-override__label">Address</span>
                        <input
                          type="text"
                          value={siteAddressOverride}
                          onChange={(e) => setSiteAddressOverride(e.target.value)}
                          placeholder="Leave blank to use project address"
                          className="sidebar-override__input"
                        />
                      </label>
                    )}
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">PO Number</span>
                      <input
                        type="text"
                        value={poNumber}
                        onChange={(e) => setPoNumber(e.target.value)}
                        placeholder="Leave blank to use project/stage PO"
                        className="sidebar-override__input"
                      />
                    </label>
                    <label className="sidebar-override">
                      <span className="sidebar-override__label">Progress Claim Number</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={(() => {
                          if (claimTitle === FINAL_CLAIM_TITLE) return '';
                          const m = /^Progress Claim (\d+)$/.exec(claimTitle.trim());
                          return m ? m[1] : '';
                        })()}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            setClaimTitle('');
                          } else {
                            const n = parseInt(v, 10);
                            if (!isNaN(n) && n > 0) {
                              setClaimTitle(`Progress Claim ${n}`);
                            }
                          }
                        }}
                        disabled={claimTitle === FINAL_CLAIM_TITLE}
                        placeholder={`Auto: ${invoiceData?.invoice_position ?? 1}`}
                        title={claimTitle === FINAL_CLAIM_TITLE
                          ? 'Disabled — invoice is marked Final Claim'
                          : 'Override the auto-numbered claim — useful when migrating a project that already has historical claims'}
                        className="sidebar-override__input"
                      />
                    </label>
                  </div>
                </div>
                </>
              )}

              {activeTab === 'progress' && progressContext && (
                <div className="sidebar-section">
                  <h3 className="sidebar-section__title">Progress Claim</h3>
                  <p className="sidebar-section__hint sidebar-section__hint--subtext">
                    Enter cumulative % to date for each phase, and the count claimed this period for items. Apply replaces existing progress claim lines.
                  </p>
                  {activePhases.length === 0 && activeItems.length === 0 ? (
                    <p className="sidebar-section__hint">No fee phases or items with values on the project's accepted fee plan.</p>
                  ) : (
                    <>
                      {activePhases.length > 0 && (
                      <div className="progress-claim-table">
                        <div className="progress-claim-row progress-claim-row--header">
                          <span>Quoted Fee</span>
                          <span>Prior %</span>
                          <span>This Claim %</span>
                          <span>This Period $</span>
                        </div>
                        {(activePhases.some(p => normDisc(p.discipline) === 'civil')
                          ? DISCIPLINES
                              .map(d => ({ d: d as Discipline | null, phases: activePhases.filter(p => normDisc(p.discipline) === d) }))
                              .filter(g => g.phases.length > 0)
                          : [{ d: null as Discipline | null, phases: activePhases }]
                        ).map(group => (
                          <React.Fragment key={group.d ?? 'all'}>
                            {group.d && (
                              <div className="progress-claim-discipline-heading">{disciplineLabel(group.d)}</div>
                            )}
                            {group.phases.map(phase => {
                              // Match by section_id so multiple Custom phases don't collide.
                              const claim = progressClaims.find(c => c.section_id === phase.section_id);
                              const cumulative = claim?.percent_complete ?? phase.prior_percent_complete;
                              const amountToDate = (phase.quoted_fee * cumulative) / 100;
                              const thisPeriod = amountToDate - phase.prior_amount_to_date;
                              // Probable = quoted but not awarded. The row is
                              // still shown (the scope exists and the client
                              // should see it), but it cannot be claimed until
                              // Probable is unticked on the fee. The server
                              // refuses it either way — see
                              // enforce_probable_not_claimed — so this is the
                              // readable half of the same rule, not the rule.
                              const probable = !!phase.is_probable;
                              return (
                                <div
                                  key={phase.section_id}
                                  className={`progress-claim-phase${probable ? ' progress-claim-phase--probable' : ''}`}
                                >
                                  <div className="progress-claim-phase__name">
                                    {phaseDisplayLabel(phase.phase, phase.section_name)}
                                    {probable && (
                                      <span
                                        className="progress-claim-phase__probable-tag"
                                        title="Marked probable on the fee — untick Probable on that phase to claim against it."
                                      >
                                        Probable
                                      </span>
                                    )}
                                  </div>
                                  <div className="progress-claim-row">
                                    <span>{formatCurrency(phase.quoted_fee)}</span>
                                    <span>{phase.prior_percent_complete.toFixed(0)}%</span>
                                    <ProgressNumberInput
                                      className="progress-claim-input"
                                      aria-label="% this claim"
                                      value={claim?.percent_complete ?? 0}
                                      onCommit={(v) => setPhasePercent(phase, v)}
                                      disabled={probable}
                                      title={probable ? 'Marked probable on the fee — untick Probable on that phase to claim against it.' : undefined}
                                    />
                                    <span>{formatCurrency(thisPeriod)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                      )}
                      {activeItems.length > 0 && (
                      <div className="progress-claim-table progress-claim-table--items">
                        <div className="progress-claim-items-title">Items — claim by count</div>
                        <div className="progress-claim-row progress-claim-row--header">
                          <span>Rate</span>
                          <span>Prior</span>
                          <span>To date</span>
                          <span>This period</span>
                        </div>
                        {(activeItems.some(it => normDisc(it.discipline) === 'civil')
                          ? DISCIPLINES
                              .map(d => ({ d: d as Discipline | null, items: activeItems.filter(it => normDisc(it.discipline) === d) }))
                              .filter(g => g.items.length > 0)
                          : [{ d: null as Discipline | null, items: activeItems }]
                        ).map(group => (
                          <React.Fragment key={group.d ?? 'all'}>
                            {group.d && (
                              <div className="progress-claim-discipline-heading">{disciplineLabel(group.d)}</div>
                            )}
                            {group.items.map(it => {
                              // Same rule as the phase rows above: an item
                              // section can be probable too (an allowance for
                              // inspections we may never be engaged for).
                              const probable = !!it.is_probable;
                              return (
                              <div
                                key={it.section_id}
                                className={`progress-claim-phase${probable ? ' progress-claim-phase--probable' : ''}`}
                              >
                                <div className="progress-claim-phase__name">
                                  {it.description || phaseDisplayLabel(it.phase, it.section_name)} (of {it.qty_total})
                                  {probable && (
                                    <span
                                      className="progress-claim-phase__probable-tag"
                                      title="Marked probable on the fee — untick Probable on that phase to claim against it."
                                    >
                                      Probable
                                    </span>
                                  )}
                                </div>
                                <div className="progress-claim-row">
                                  <span>{formatCurrency(it.rate)}</span>
                                  <span>{it.prior_qty_claimed}</span>
                                  <ProgressNumberInput
                                    className="progress-claim-input"
                                    aria-label="Count this claim"
                                    value={it.count || 0}
                                    onCommit={(v) => setItemCount(it, v)}
                                    max={it.qty_total}
                                    disabled={probable}
                                    title={probable ? 'Marked probable on the fee — untick Probable on that phase to claim against it.' : undefined}
                                  />
                                  <span>{it.count - it.prior_qty_claimed}</span>
                                </div>
                              </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                      )}
                      <button
                        className="sidebar-btn sidebar-btn--primary"
                        onClick={applyProgressClaim}
                        type="button"
                      >
                        Apply Progress Claim
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showDeleteTaxInvoiceConfirm && createPortal(
        <div
          className="delete-confirm-overlay"
          // Editor renders inside `.invoice-editor-fullscreen` (z-index 9999).
          // The portal lands on document.body, so we need to clear that stack
          // explicitly — the shared SCSS uses z-index:1060 which loses to the
          // editor wrapper.
          style={{ zIndex: 10000 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteTaxInvoiceConfirm(false);
            }
          }}
        >
          <div
            className="delete-confirm-modal"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete tax invoice</h3>
            <p>
              This will remove all line items and progress claims for this tax invoice document.
              The invoice entry stays in the register with its current total — you can edit it via the
              detail modal or rebuild the tax invoice later.
            </p>
            <p className="project-name">
              <strong>{invoice.invoice_number}</strong>
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowDeleteTaxInvoiceConfirm(false);
                }}
                className="btn-secondary"
                disabled={isDeletingTaxInvoice}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  void handleDeleteTaxInvoice();
                }}
                className="btn-danger"
                disabled={isDeletingTaxInvoice}
              >
                {isDeletingTaxInvoice ? 'Deleting...' : 'Delete tax invoice'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};



export default InvoiceEditor;
